import { useMemo } from "react";
import { useSelector } from "react-redux";
import { selectStoreConfig } from "../redux/Slice/siteSlice";

// The storefront's wording for each store type (Store.template; the list and the sample catalogs are in the backend's
// services/starterCatalog). Every store runs this one storefront, so text that depends on what a store sells lives
// here instead of in the components. Unknown types use the plants wording.
const TYPES = {
    custom: {
        color: "#0f766e",
        gradient: ["#0f766e", "#14b8a6"],
        badgeTop: "Featured pick",
        badgeBottom: "for your lifestyle",
        icon: "✨",
        item: "product",
        items: "products",
        heroEyebrow: "NEW ARRIVALS • BEST SELLERS • GIFT IDEAS",
        heroCta: "Shop collection",
        heroStats: ["✅ Quality checked", "🚚 Delivery across India"],
        searchPlaceholder: "Search products & collections...",
        featuredTitle: "Featured products",
        featuredText: "Discover top quality items crafted for you.",
        readyLine: "products ready for delivery.",
        tagline: "Quality products delivered to your door",
        footerNote: "✨ Premium quality products directly to you.",
        guestLine: "Find something special for your style",
        accountLine: "Your personal customer account",
        shopperName: "Valued Customer",
        welcome: "Welcome to our store ✨",
        cartTitle: "Your cart",
        cartEmptyIcon: "🛍️",
        checkoutHint: "Tell us where to deliver your order.",
        orderDoneLine: "Your order is confirmed and being prepared.",
        reviewIntro: "Your honest feedback helps other customers shop with confidence.",
        infoTitle: "Customer Care",
        infoLinks: ["Shipping Policy", "Delivery", "Contact Us", "Returns & Exchanges"],
        detailIntro: "Carefully selected product with verified quality and specifications.",
        detailBadge: ["✅", "Quality checked", "Packed with care"],
        detailNote: "Checked before dispatch",
        aboutTitle: "About this product",
        aboutText: "Crafted with attention to detail and designed for daily delight.",
        care: [
            ["✅", "Quality Checked", "Every order is checked and packed with care."],
            ["🚚", "Fast Shipping", "Safe packaging and swift doorstep delivery."],
            ["💬", "Support", "Reach out via WhatsApp anytime with order questions."],
        ],
        categoryIcons: { "New Arrivals": "✨", "Best Sellers": "🔥", "Gift Ideas": "🎁", "Special Offers": "🏷️", "Featured Products": "⭐" },
        occasionsTitle: "Shop by category",
        occasionsEyebrow: "✨ Curated selections for you",
        occasionsSubtitle: "Explore our latest arrivals and top-rated items.",
        occasions: [["✨", "New Arrivals"], ["🔥", "Best Sellers"], ["🎁", "Gift Ideas"], ["🏷️", "Special Offers"]],
        budgetPrefix: "Find items under",
        budgets: [499, 999, 1999],
        completeTitle: "Frequently bought together",
    },
    plants: {
        badgeTop: "Fresh pick",
        badgeBottom: "for your space",
        icon: "🌿",
        item: "plant",
        items: "plants",
        heroEyebrow: "PLANTS • POTS • HAPPY SPACES",
        heroCta: "Shop plants",
        heroStats: ["💚 Easy care", "🚚 Safe delivery"],
        searchPlaceholder: "Search plants, pots, seeds & more",
        featuredTitle: "Featured plants",
        featuredText: "Find the perfect green companion for your space.",
        readyLine: "plants ready for your space.",
        tagline: "Grow a little happiness",
        footerNote: "🌿 A greener home starts with one plant.",
        guestLine: "Find something green for your space",
        accountLine: "Your plant-loving account",
        shopperName: "Plant Parent",
        welcome: "Welcome, plant parent 🌿",
        cartTitle: "Your plant cart",
        cartEmptyIcon: "🪴",
        checkoutHint: "Tell us where to deliver your plants.",
        orderDoneLine: "Your plants are on their way to a happier home.",
        reviewIntro: "Your honest feedback helps other plant lovers shop with confidence.",
        infoTitle: "Plant Care",
        infoLinks: ["Care guides", "Delivery", "Contact", "Returns"],
        detailIntro: "Bring natural beauty into your home with this carefully selected plant.",
        detailBadge: ["🌿", "Healthy plant", "Carefully selected"],
        detailNote: "Plant-care friendly selection",
        aboutTitle: "About this plant",
        aboutText: "Place your plant in a suitable spot, check the soil before watering, and adjust care to your room's light and temperature.",
        care: [
            ["☀️", "Light", "Bright, suitable indirect light is a good starting point."],
            ["💧", "Water", "Check the topsoil before watering; avoid leaving roots waterlogged."],
            ["🪴", "Care tip", "Use a pot with drainage and keep leaves clean."],
        ],
        categoryIcons: { "Low Light": "☁️", Succulents: "🌵", Flowering: "🌸", Outdoor: "🌴", Indoor: "🪴" },
        occasionsTitle: "Shop by space & vibe",
        occasionsEyebrow: "🌿 Greenery for every corner",
        occasionsSubtitle: "Find the ideal indoor, outdoor or air-purifying plant.",
        occasions: [
            ["🪴", "Indoor Decor"], ["☁️", "Low Light"], ["🍃", "Air Purifying"], ["🌵", "Succulents"],
            ["🌸", "Flowering"], ["☀️", "Balcony & Sun"], ["💼", "Desk Plants"], ["🎁", "Plant Gifts"],
            ["🌱", "Easy Care"], ["🐾", "Pet Friendly"],
        ],
        budgetPrefix: "Greens under",
        budgets: [299, 499, 999],
        completeTitle: "Complete your green corner",
    },
    pets: {
        // Bright "gaming" yellow while the store keeps the design's colour (backend starterCatalog/pets.json); being
        // light, it fills buttons with dark text on them and text uses a near-black shade (utils/theme.js)
        color: "#fff323",
        gradient: ["#fff323", "#ffd60a"],
        badgeTop: "Picked with love",
        badgeBottom: "for happy pets",
        icon: "🐾",
        item: "product",
        items: "pet products",
        heroEyebrow: "FOOD • TOYS • CARE",
        heroCta: "Shop now",
        heroStats: ["🦴 Treats galore", "🚚 Fast delivery"],
        searchPlaceholder: "Search dog food, cat toys, grooming & more",
        featuredTitle: "Featured products",
        featuredText: "Loved by dogs, cats and their humans.",
        readyLine: "picks for happy pets.",
        tagline: "Happy pets, happy homes",
        footerNote: "🐾 Every tail deserves the best.",
        guestLine: "Treats, toys and care for your pet",
        accountLine: "Your pet parent account",
        shopperName: "Pet Parent",
        welcome: "Welcome, pet parent 🐾",
        cartTitle: "Your pet cart",
        cartEmptyIcon: "🦴",
        checkoutHint: "Tell us where to deliver your pet supplies.",
        orderDoneLine: "Tails are wagging already. Your order is on its way!",
        reviewIntro: "Your honest feedback helps other pet parents shop with confidence.",
        infoTitle: "Pet Care",
        infoLinks: ["Feeding guides", "Delivery", "Contact", "Returns"],
        detailIntro: "Chosen with care for healthy, happy pets.",
        detailBadge: ["🐾", "Pet approved", "Safe and trusted"],
        detailNote: "Vet-friendly selection",
        aboutTitle: "About this product",
        aboutText: "Introduce new food slowly over a few days, keep fresh water nearby, and check the size guide for toys and accessories.",
        care: [
            ["🥣", "Feeding", "Follow the pack's feeding guide for your pet's weight."],
            ["💧", "Water", "Always keep a bowl of fresh water within reach."],
            ["🩺", "Health tip", "Ask your vet before changing diets for puppies or seniors."],
        ],
        categoryIcons: { "Dog Food": "🐶", "Cat Food": "🐱", Toys: "🎾", Grooming: "🛁", Accessories: "🦮" },
        occasionsTitle: "Shop by pet & mood",
        occasionsEyebrow: "🐾 For your fur babies",
        occasionsSubtitle: "Tasty treats, durable toys and grooming care for dogs & cats.",
        occasions: [
            ["🐶", "Dog Food"], ["🐱", "Cat Treats"], ["🎾", "Chew & Toys"], ["🛁", "Grooming & Bath"],
            ["🦮", "Leash & Collars"], ["🦴", "Puppy Special"], ["🐈", "Kitten Care"], ["🛏️", "Beds & Mats"],
            ["🦷", "Dental Chews"], ["🎁", "Pet Hampers"],
        ],
        budgetPrefix: "Treats under",
        budgets: [249, 499, 999],
        completeTitle: "Complete your pet's happiness",
    },
    aquarium: {
        badgeTop: "Fresh stock",
        badgeBottom: "for your tank",
        icon: "🐠",
        item: "product",
        items: "aquarium products",
        heroEyebrow: "FISH • PLANTS • TANKS",
        heroCta: "Shop aquarium",
        heroStats: ["🌿 Live plants", "📦 Safe live delivery"],
        searchPlaceholder: "Search fish, plants, tanks & more",
        featuredTitle: "Featured picks",
        featuredText: "Colourful fish and everything for a healthy tank.",
        readyLine: "picks for a beautiful aquarium.",
        tagline: "A little ocean at home",
        footerNote: "🐠 Happy fish, healthy tanks.",
        guestLine: "Fish, plants and tank essentials",
        accountLine: "Your aquarist account",
        shopperName: "Fish Keeper",
        welcome: "Welcome, fish keeper 🐠",
        cartTitle: "Your aquarium cart",
        cartEmptyIcon: "🐟",
        checkoutHint: "Tell us where to deliver your order.",
        orderDoneLine: "Your order is packed with care and on its way.",
        reviewIntro: "Your honest feedback helps other fish keepers shop with confidence.",
        infoTitle: "Fish Care",
        infoLinks: ["Setup guides", "Live delivery", "Contact", "Returns"],
        detailIntro: "Healthy, carefully kept stock for a thriving aquarium.",
        detailBadge: ["🐠", "Healthy stock", "Checked before dispatch"],
        detailNote: "Live arrival guaranteed",
        aboutTitle: "About this product",
        aboutText: "Float new fish in their bag for 15 minutes before release, and keep up small weekly water changes.",
        care: [
            ["🌡️", "Temperature", "Most tropical fish like 24–28°C water."],
            ["💧", "Water", "Change 20% of the water every week."],
            ["🍽️", "Feeding", "Feed small amounts once or twice a day."],
        ],
        categoryIcons: { Fish: "🐟", "Aquarium Plants": "🌿", "Tanks & Bowls": "🫙", "Fish Food": "🍽️", "Decor & Care": "🪸" },
        occasionsTitle: "Shop by tank & aquatic setup",
        occasionsEyebrow: "🐠 Thriving underwater worlds",
        occasionsSubtitle: "Vibrant live fish, lush plants and crystal-clear tank essentials.",
        occasions: [
            ["🐟", "Colorful Fish"], ["🌿", "Live Plants"], ["🫙", "Nano Bowls"], ["🍽️", "Premium Food"],
            ["🪸", "Driftwood & Rocks"], ["⚡", "Filters & Air"], ["💡", "LED Tank Lights"], ["🫧", "Water Care"],
            ["🦐", "Shrimp & Snails"], ["🪴", "Aquascaping"],
        ],
        budgetPrefix: "Aquatics under",
        budgets: [199, 499, 1499],
        completeTitle: "Complete your aquarium setup",
    },
    gifts: {
        badgeTop: "Wrapped with love",
        badgeBottom: "for someone special",
        icon: "🎁",
        item: "gift",
        items: "gifts",
        heroEyebrow: "PERSONALISED • DIWALI • SURPRISES",
        heroCta: "Shop gifts",
        heroStats: ["✍️ Name, photo or message", "🎀 Free gift wrap"],
        searchPlaceholder: "Search custom mugs, photo frames, hampers & more",
        featuredTitle: "Featured gifts",
        featuredText: "Customised gifts and thoughtful picks for every occasion.",
        readyLine: "gifts ready to make someone smile.",
        tagline: "Made just for them",
        footerNote: "🎁 Every gift tells a story.",
        guestLine: "Find the perfect gift",
        accountLine: "Your gifting account",
        shopperName: "Gift Giver",
        welcome: "Welcome back 🎁",
        cartTitle: "Your gift cart",
        cartEmptyIcon: "🎀",
        checkoutHint: "Tell us where to deliver your gift.",
        orderDoneLine: "Your gift is being wrapped and will be on its way soon.",
        reviewIntro: "Your honest feedback helps others choose the perfect gift.",
        infoTitle: "Gifting",
        infoLinks: ["Gift guides", "Delivery", "Contact", "Returns"],
        detailIntro: "Beautifully packed and ready to gift.",
        detailBadge: ["🎁", "Gift ready", "Wrapped with care"],
        detailNote: "Add the name, photo or message at checkout",
        aboutTitle: "About this gift",
        aboutText: "Every gift is made and packed by hand. Tell us the name, photo or message at checkout and we'll personalise it for you.",
        care: [
            ["🎀", "Wrapping", "Free gift wrap on every order."],
            ["✍️", "Personalise", "Add the name, photo or message at checkout."],
            ["📅", "Timing", "Order a day early for important dates."],
        ],
        noteLabel: "Personalise your gift (optional)",
        notePlaceholder: "Name, message or date to print, e.g. \"Happy Birthday Riya\".",
        categoryIcons: {
            "Birthday Gifts": "🎂", "Anniversary Gifts": "💍", "Wedding Gifts": "💒", "Valentine's Gifts": "❤️",
            "Customized Gifts": "🎨", "Gift Hampers": "🧺", Flowers: "💐", Chocolates: "🍫", "Candles & Decor": "🕯️", "Gift Boxes": "🎁",
        },
        // The modern look's extras (FashionHome.js): shop by occasion (product tags) and by budget, and the product
        // page's suggestions row
        occasions: [["🪔", "Diwali"], ["🎂", "Birthday"], ["💍", "Anniversary"], ["❤️", "Valentine's"], ["👨", "For Him"], ["👩", "For Her"], ["🧸", "For Kids"], ["🏡", "Housewarming"], ["🙏", "Thank You"], ["✍️", "Personalised"]],
        budgets: [499, 999, 1999],
        completeTitle: "Complete the gift",
    },
    grocery: {
        badgeTop: "Fresh deals",
        badgeBottom: "for your kitchen",
        icon: "🛒",
        item: "item",
        items: "items",
        heroEyebrow: "DAILY ESSENTIALS • GREAT PRICES",
        heroCta: "Shop groceries",
        heroStats: ["💰 Weekly offers", "⚡ Quick delivery"],
        searchPlaceholder: "Search rice, snacks, drinks & more",
        featuredTitle: "Featured items",
        featuredText: "Everyday essentials at great prices.",
        readyLine: "items for your kitchen and home.",
        tagline: "Your daily essentials",
        footerNote: "🛒 Fresh, fast and fairly priced.",
        guestLine: "Groceries delivered to your door",
        accountLine: "Your grocery account",
        shopperName: "Shopper",
        welcome: "Welcome back 🛒",
        cartTitle: "Your grocery basket",
        cartEmptyIcon: "🧺",
        checkoutHint: "Tell us where to deliver your groceries.",
        orderDoneLine: "Your groceries are being packed and will reach you soon.",
        reviewIntro: "Your honest feedback helps other shoppers choose well.",
        infoTitle: "Help",
        infoLinks: ["Delivery slots", "Delivery", "Contact", "Returns"],
        detailIntro: "Quality checked and packed fresh for your kitchen.",
        detailBadge: ["✅", "Quality checked", "Packed fresh"],
        detailNote: "Best price promise",
        aboutTitle: "About this item",
        aboutText: "Store in a cool, dry place and check the pack for the best-before date.",
        care: [
            ["📦", "Storage", "Keep in a cool, dry place once opened."],
            ["📅", "Freshness", "Check the best-before date on the pack."],
            ["♻️", "Packaging", "Recycle the outer pack where you can."],
        ],
        categoryIcons: { Staples: "🍚", Snacks: "🍪", Beverages: "🥤", "Dairy & Breakfast": "🥛", Household: "🧽" },
        occasionsTitle: "Shop by kitchen essentials",
        occasionsEyebrow: "🛒 Daily pantry must-haves",
        occasionsSubtitle: "Everyday grains, morning breakfast, snacks and household staples.",
        occasions: [
            ["🍚", "Rice & Flours"], ["🍪", "Tea-Time Snacks"], ["🥤", "Beverages"], ["🥛", "Breakfast Cereals"],
            ["🧽", "Home & Clean"], ["🫒", "Oils & Ghee"], ["🧂", "Spices & Masala"], ["🍝", "Pasta & Noodles"],
            ["🍫", "Choco & Spreads"], ["⚡", "Daily Deals"],
        ],
        budgetPrefix: "Pantry picks under",
        budgets: [99, 199, 499],
        completeTitle: "Complete your daily pantry",
    },
    fresh: {
        badgeTop: "Picked today",
        badgeBottom: "from the farm",
        icon: "🥬",
        item: "item",
        items: "fresh picks",
        heroEyebrow: "FARM FRESH • PICKED TODAY",
        heroCta: "Shop fresh",
        heroStats: ["🌱 Picked this morning", "🚚 Morning delivery"],
        searchPlaceholder: "Search fruits, vegetables, greens & more",
        featuredTitle: "Fresh today",
        featuredText: "Seasonal fruits and vegetables, picked this morning.",
        readyLine: "fresh picks for your kitchen.",
        tagline: "Fresh from the farm",
        footerNote: "🥬 Picked today, on your table tomorrow.",
        guestLine: "Farm-fresh fruits and vegetables",
        accountLine: "Your fresh-food account",
        shopperName: "Fresh Food Lover",
        welcome: "Welcome back 🥬",
        cartTitle: "Your fresh basket",
        cartEmptyIcon: "🧺",
        checkoutHint: "Tell us where to deliver your fresh produce.",
        orderDoneLine: "Your fresh produce will be picked and delivered soon.",
        reviewIntro: "Your honest feedback helps others pick the freshest produce.",
        infoTitle: "Freshness",
        infoLinks: ["Storage tips", "Delivery", "Contact", "Returns"],
        detailIntro: "Hand-picked for freshness and flavour.",
        detailBadge: ["🥬", "Farm fresh", "Picked this morning"],
        detailNote: "Fresh or we replace it",
        aboutTitle: "About this item",
        aboutText: "Wash before use, and keep leafy greens and cut fruit in the fridge.",
        care: [
            ["🧊", "Storage", "Refrigerate greens; keep bananas and onions outside."],
            ["🚿", "Washing", "Rinse well under running water before use."],
            ["⏱️", "Best within", "Enjoy within 2–4 days for the best taste."],
        ],
        categoryIcons: { Fruits: "🍎", Vegetables: "🥕", "Leafy Greens": "🥬", Exotic: "🥭", "Dry Fruits": "🥜" },
        occasionsTitle: "Shop by farm fresh harvest",
        occasionsEyebrow: "🥬 Direct from local farms",
        occasionsSubtitle: "Crisp vegetables, juicy seasonal fruits and chemical-free greens.",
        occasions: [
            ["🍎", "Sweet Fruits"], ["🥕", "Daily Veggies"], ["🥬", "Leafy Greens"], ["🥭", "Exotic Picks"],
            ["🥜", "Dry Fruits"], ["🍋", "Citrus & Detox"], ["🥗", "Salad Essentials"], ["🧄", "Ginger & Garlic"],
            ["🌽", "Farm Sweetcorn"], ["🧺", "Weekly Basket"],
        ],
        budgetPrefix: "Fresh picks under",
        budgets: [99, 199, 399],
        completeTitle: "Complete your farm fresh basket",
    },
    selfcare: {
        badgeTop: "Artisanal & Pure",
        badgeBottom: "small-batch luxury",
        icon: "🫧",
        item: "product",
        items: "handcrafted products",
        heroEyebrow: "HANDCRAFTED • BOTANICAL • SMALL BATCH",
        heroCta: "Explore soaps & scents",
        heroStats: ["🌿 100% Toxin-Free", "🚚 Fast Delivery"],
        searchPlaceholder: "Search artisanal soaps, luxury perfumes, handwash & hampers",
        searchPrompt: "Looking for a signature scent or nourishing bar?",
        featuredTitle: "Artisanal Bestsellers",
        featuredText: "Cold-pressed soaps, long-lasting perfumes and luxury bath essentials loved by our community.",
        readyLine: "handcrafted self-care picks ready to ship.",
        tagline: "Luxury in every lather, elegance in every drop",
        footerNote: "🫧 Handcrafted in small batches • Pure botanical luxury • Cruelty-free.",
        guestLine: "Handmade soaps, perfumes & bath care",
        accountLine: "Your self-care account",
        shopperName: "Fragrance Lover",
        welcome: "Welcome to your scent ritual ✨",
        cartTitle: "Your self-care bag",
        cartEmptyIcon: "🫧",
        checkoutHint: "Tell us where to deliver your artisanal package.",
        orderDoneLine: "Your handcrafted self-care treats are being packed with love.",
        reviewIntro: "Your feedback on aroma, lather and feel helps fellow self-care lovers shop with confidence.",
        infoTitle: "Botanical Purity",
        infoLinks: ["Scent guide", "Ingredients", "Delivery", "Returns"],
        detailIntro: "Crafted in small batches with cold-pressed botanical oils, rich plant butters, and fine fragrances.",
        detailBadge: ["🫧", "Hand-Poured", "Pure Botanical Oils"],
        detailNote: "Zero parabens, SLS or harsh sulfates • 100% Cruelty-free",
        aboutTitle: "The Scent & Ritual",
        aboutText: "Keep soaps on a well-draining dish to extend their life. Apply perfume to warm pulse points (wrists, neck, collarbone) after moisturizing for maximum longevity and sillage.",
        care: [
            ["🧴", "How to use", "Lather soaps with warm water for creamy foam; spritz perfume on warm pulse points."],
            ["☀️", "Storage tip", "Keep soap bars drained between uses; store perfumes in a cool spot away from sunlight."],
            ["🌿", "Clean beauty", "Free from parabens, phthalates, SLS and animal testing."],
        ],
        categoryIcons: {
            "Handmade Soaps": "🧼", Perfumes: "🌸", Handwash: "🫧", "Body Care": "🧴", "Gift Sets": "🎁",
            "Body Mists": "✨", "Bath Salts": "🛁", "Essential Oils": "💧", "Face Care": "🪞",
        },
        // Modern look extras (FashionHome.js): shop by scent family / mood and by budget
        occasionsTitle: "Shop by scent & mood",
        occasionsEyebrow: "✨ Fragrance families",
        occasionsSubtitle: "Find your signature aroma or artisanal bar.",
        occasions: [
            ["🌹", "Floral & Rose"], ["🪵", "Oudh & Woody"], ["🍋", "Fresh Citrus"], ["🍦", "Warm Vanilla"],
            ["🌿", "Herbal & Neem"], ["☕", "Coffee & Scrub"], ["🌙", "Lavender Calm"], ["🌊", "Aqua & Ocean"],
            ["🪔", "Sandalwood & Amber"], ["🎁", "Gift Hampers"],
        ],
        budgetPrefix: "Treats under",
        budgets: [299, 599, 999],
        completeTitle: "Complete your daily ritual",
    },
    clothing: {
        badgeTop: "New season",
        badgeBottom: "just landed",
        icon: "👗",
        item: "style",
        items: "styles",
        heroEyebrow: "NEW DROP • JUST LANDED",
        heroCta: "Shop the drop",
        heroStats: ["📏 Easy size exchange", "🚚 Free delivery"],
        searchPlaceholder: "Search kurtis, sarees, co-ords & more",
        searchPrompt: "What are you shopping for today?",
        featuredTitle: "Trending now",
        featuredText: "The styles everyone is loving this week.",
        readyLine: "styles ready for you.",
        tagline: "Style that feels like you",
        footerNote: "👗 Wear what makes you happy.",
        guestLine: "Fresh styles for everyone",
        accountLine: "Your style account",
        shopperName: "Style Lover",
        welcome: "Welcome back 👗",
        cartTitle: "Your shopping bag",
        cartEmptyIcon: "🛍️",
        checkoutHint: "Tell us where to deliver your order.",
        orderDoneLine: "Your new styles are on their way.",
        reviewIntro: "Your honest feedback on fit and quality helps other shoppers.",
        infoTitle: "Help",
        infoLinks: ["Size guide", "Delivery", "Contact", "Exchanges"],
        detailIntro: "Quality fabric and a comfortable fit for every day.",
        detailBadge: ["👕", "Quality fabric", "Checked before dispatch"],
        detailNote: "Easy 7-day size exchange",
        aboutTitle: "About this style",
        aboutText: "Check the size guide before ordering. Wash dark colours separately the first time.",
        care: [
            ["📏", "Fit", "True to size. Size up for a relaxed fit."],
            ["🧺", "Wash", "Gentle machine wash in cold water."],
            ["🔄", "Exchange", "Free size exchange within 7 days."],
        ],
        categoryIcons: {
            Kurtis: "👚", Sarees: "🥻", "Salwar Suits": "🪷", Lehengas: "💃", "Co-ords & Dresses": "👗", "Leggings & Nightwear": "🌙",
            "Oversized Tees": "👕", "Kids Ethnic": "🧸", Men: "👔", Women: "👗", Kids: "🧸", "Ethnic Wear": "🪔", Accessories: "👜",
        },
        occasionsTitle: "Shop by style & occasion",
        occasionsEyebrow: "✨ Trendsetters' curation",
        occasionsSubtitle: "From festive wedding glam to effortless casuals and daily fits.",
        occasions: [
            ["🥻", "Festive Glam"], ["💍", "Wedding Ready"], ["☕", "Daily Casuals"], ["💼", "Office Chic"],
            ["✨", "Party Wear"], ["🌙", "Loungewear"], ["👗", "Summer Dresses"], ["🌸", "Floral Kurtis"],
            ["🧸", "Kids Specials"], ["👜", "Statement Looks"],
        ],
        budgetPrefix: "Looks under",
        budgets: [599, 999, 1999],
        completeTitle: "Complete the look",
    },
    tech: {
        color: "#6d28d9",
        gradient: ["#6d28d9", "#06b6d4"],
        badgeTop: "Phone cases",
        badgeBottom: "for all phone models",
        icon: "⚡",
        item: "skin case",
        items: "custom phone cases",
        heroEyebrow: "PRINTED • SILICONE • LEATHER • CUSTOM PHOTO CASES",
        heroCta: "Shop phone cases",
        heroStats: ["✨ 3M bubble-free vinyl", "📷 Live photo skin preview"],
        searchPlaceholder: "Search printed, silicone, leather & rugged cases",
        searchPrompt: "Find the perfect case for your phone or design your own",
        featuredTitle: "Trending phone cases",
        featuredText: "Printed, silicone, leather and rugged cases with precise cutouts for 100+ phone models.",
        readyLine: "phone cases ready for your device.",
        tagline: "Phone cases & accessories",
        footerNote: "⚡ Premium 3M vinyl skins & scratch protection.",
        guestLine: "Printed, leather & custom photo phone cases",
        accountLine: "Your custom skins studio account",
        shopperName: "Skin Designer",
        welcome: "Welcome back ⚡",
        cartTitle: "Your skin vault",
        cartEmptyIcon: "📱",
        checkoutHint: "Tell us where to deliver your custom skin cases.",
        orderDoneLine: "Your custom phone skins are being printed and packed.",
        reviewIntro: "Your feedback on sticker print quality and fit helps other shoppers customize.",
        infoTitle: "Skin Application",
        infoLinks: ["Application tutorial", "Supported models", "Contact", "Custom print guide"],
        detailIntro: "Precise cutouts, raised camera and screen edges, and shock-absorbing corners.",
        detailBadge: ["⚡", "3M bubble-free vinyl", "Live skin preview"],
        detailNote: "Residue-free removal guarantee",
        aboutTitle: "About this skin case",
        aboutText: "Clean phone surface with the included alcohol wipe. Align with camera cutouts first, then smooth out towards corners with gentle heat.",
        care: [
            ["✨", "3M Vinyl", "Patented micro-groove adhesive ensures zero air bubbles during application."],
            ["🛡️", "Scratch Shield", "Matte protective laminate defends against scratches and everyday scuffs."],
            ["🧼", "Zero Residue", "Removes cleanly without leaving any sticky residue behind."],
        ],
        categoryIcons: {
            "Printed & Floral Cases": "🌸",
            "Silicone & Matte Cases": "🎨",
            "Leather & Premium Cases": "👜",
            "Wood, Clear & Rugged Cases": "🛡️",
            "Phone Accessories": "🔌",
        },
        occasionsTitle: "Shop by case style",
        occasionsEyebrow: "🔥 Trending case collections",
        occasionsSubtitle: "From floral prints and pastel silicone to premium leather, rugged armor and accessories.",
        occasions: [
            ["🌸", "Printed"], ["🎨", "Silicone"], ["👜", "Leather"], ["🛡️", "Rugged"],
            ["💎", "Clear"], ["🌳", "Wood"], ["🔋", "Power Bank"], ["🎧", "Earphones"],
        ],
        budgetPrefix: "Cases under",
        budgets: [399, 599, 799],
        completeTitle: "Add a matching accessory",
    },
    footwear: {
        color: "#e11d48",
        gradient: ["#e11d48", "#fb923c"],
        badgeTop: "Handcrafted comfort",
        badgeBottom: "juttis, heels & sneakers",
        icon: "👡",
        item: "pair",
        items: "footwear",
        heroEyebrow: "JUTTIS • HEELS • SNEAKERS • MEN'S LEATHER",
        heroCta: "Shop handcrafted collection",
        heroStats: ["☁️ Double cushioned", "🔄 7-day size exchange"],
        searchPlaceholder: "Search juttis, bellies, heels, sneakers & loafers",
        searchPrompt: "Find your dream pair for every occasion",
        featuredTitle: "Trending pairs",
        featuredText: "Double-cushioned comfort juttis, bespoke painted sneakers and artisanal velvet bags.",
        readyLine: "handcrafted pairs & bags ready for your celebrations.",
        tagline: "Comfort in every step, elegance in every bag",
        footerNote: "👡 Handcrafted luxury that loves your feet and style.",
        guestLine: "Handcrafted comfort shoes & artisanal bags",
        accountLine: "Your artisanal footwear account",
        shopperName: "Footwear & Bag Lover",
        welcome: "Welcome back ✨",
        cartTitle: "Your artisanal bag",
        cartEmptyIcon: "👡",
        checkoutHint: "Tell us where to deliver your pairs and bags.",
        orderDoneLine: "Your handcrafted shoes and artisanal bags are on their way.",
        reviewIntro: "Your feedback on sizing and comfort helps other shoppers choose with confidence.",
        infoTitle: "Care & Sizing",
        infoLinks: ["Size guide", "Delivery", "Contact", "Exchanges"],
        detailIntro: "Cushioned footbeds, soft linings and flexible soles for all-day comfort.",
        detailBadge: ["☁️", "Double cushioned", "No shoe bite guarantee"],
        detailNote: "Easy 7-day size exchange",
        aboutTitle: "About this handcrafted piece",
        aboutText: "Check our foot length measurement guide before ordering. Store in the provided muslin dust bag.",
        care: [
            ["📏", "Sizing", "True to size. Order 1 size up if you have broad feet."],
            ["☁️", "Comfort", "Memory foam footbed conforms to your foot arch."],
            ["🔄", "Exchange", "Free size exchange within 7 days."],
        ],
        categoryIcons: {
            "Bridal & Festive Juttis": "👰",
            "Everyday Flats & Bellies": "🥿",
            "Heels & Party Wear": "👠",
            "Sneakers & Casual Shoes": "👟",
            "Men's Loafers & Formal Shoes": "👞",
        },
        occasionsTitle: "Shop by celebration",
        occasionsEyebrow: "✨ Step out in handcrafted luxury",
        occasionsSubtitle: "From bridal juttis and party heels to everyday bellies, sneakers and leather loafers.",
        occasions: [
            ["👰", "Jutti"], ["🥿", "Bellies"], ["👠", "Heels"], ["👟", "Sneakers"],
            ["👞", "Brogues"], ["✨", "Sequin"], ["🌸", "Pink"], ["👑", "Loafers"],
        ],
        budgetPrefix: "Pairs under",
        budgets: [899, 1499, 2499],
        completeTitle: "Complete the look with another pair",
    },
    bags: {
        color: "#059669",
        gradient: ["#059669", "#14b8a6"],
        badgeTop: "Bags for every plan",
        badgeBottom: "totes to party clutches",
        icon: "👜",
        item: "bag",
        items: "bags & totes",
        heroEyebrow: "TOTES • HANDBAGS • SLINGS • BACKPACKS • CLUTCHES",
        heroCta: "Shop modern carriers",
        heroStats: ["💻 Padded laptop sleeves", "🔒 Inner zip pockets"],
        searchPlaceholder: "Search totes, handbags, slings, backpacks & clutches",
        searchPrompt: "Find the aesthetic tote or bag that carries your everyday world",
        featuredTitle: "Trending bags",
        featuredText: "Canvas and leather totes, handbags, slings, laptop backpacks and party clutches.",
        readyLine: "bags ready for your everyday adventures.",
        tagline: "Carry what matters in style",
        footerNote: "👜 Heavy-duty canvas & aesthetic daily carriers.",
        guestLine: "Aesthetic totes and bags for work & everyday life",
        accountLine: "Your tote studio account",
        shopperName: "Bag Enthusiast",
        welcome: "Welcome back 🎒",
        cartTitle: "Your shopping tote",
        cartEmptyIcon: "👜",
        checkoutHint: "Tell us where to deliver your bags.",
        orderDoneLine: "Your new carrier is packed and on its way.",
        reviewIntro: "Your feedback on fabric weight, zip security and capacity helps other shoppers.",
        infoTitle: "Bag Care",
        infoLinks: ["Laptop fit guide", "Shipping", "Contact", "Returns"],
        detailIntro: "Sturdy materials, reinforced handles and inner zip pockets for everyday carry.",
        detailBadge: ["🎒", "Heavy-duty 450 GSM", "YKK zipper security"],
        detailNote: "Spacious multi-compartment interior",
        aboutTitle: "About this bag",
        aboutText: "Spot clean with damp cloth and mild soap. Air dry in shade to maintain fabric integrity.",
        care: [
            ["💻", "Capacity", "Fits laptops up to 16 inches and everyday study or work essentials."],
            ["🧼", "Cleaning", "Spot clean with damp cloth; do not machine tumble dry."],
            ["🔒", "Security", "YKK metal zipper keeps your keys, wallet and phone secure."],
        ],
        categoryIcons: {
            "Canvas & Leather Totes": "👜",
            "Handbags & Top Handles": "👛",
            "Slings & Crossbody Bags": "🥐",
            "Backpacks & Laptop Bags": "🎒",
            "Clutches & Potlis": "✨",
        },
        occasionsTitle: "Shop by aesthetic & lifestyle",
        occasionsEyebrow: "🎒 Built for work, campus & weekend travel",
        occasionsSubtitle: "From everyday canvas totes and handbags to laptop backpacks and sequin clutches.",
        occasions: [
            ["👜", "Tote"], ["👛", "Handbag"], ["🥐", "Sling"], ["🎒", "Backpack"],
            ["💻", "Laptop"], ["✨", "Clutch"], ["🌸", "Floral"], ["🖤", "Black"],
        ],
        budgetPrefix: "Bags under",
        budgets: [599, 899, 1499],
        completeTitle: "Pair with a matching clutch",
    },
    crafts: {
        color: "#ea580c",
        gradient: ["#ea580c", "#f59e0b"],
        badgeTop: "Handmade keepsakes",
        badgeBottom: "made by Indian artisans",
        icon: "🎨",
        item: "keepsake",
        items: "handcrafted keepsakes",
        heroEyebrow: "MACRAME • DRIED FLOWERS • HAND-PAINTED POTTERY",
        heroCta: "Shop handcrafted treasures",
        heroStats: ["🪢 Hand-knotted macrame", "🎨 Hand-painted by artisans"],
        searchPlaceholder: "Search macrame, dried flowers, pottery & wooden crafts",
        searchPrompt: "Find unique personalized handcrafted gifts & keepsakes",
        featuredTitle: "Handmade decor & gifts",
        featuredText: "Macrame, everlasting dried bouquets, hand-painted pottery and wooden handicrafts.",
        readyLine: "keepsakes handcrafted with love for you.",
        tagline: "Art that tells your unique story",
        footerNote: "🎨 Handcrafted with passion, delivered in break-proof packaging.",
        guestLine: "Personalized handcrafted art & sentimental gifts",
        accountLine: "Your artisan studio account",
        shopperName: "Art Collector",
        welcome: "Welcome back 🎨",
        cartTitle: "Your keepsake cart",
        cartEmptyIcon: "🎁",
        checkoutHint: "Tell us where to deliver your custom order.",
        orderDoneLine: "Our artisans are handcrafting your order with care.",
        reviewIntro: "Your review supports our independent artisans and helps others find meaningful gifts.",
        infoTitle: "Custom Orders",
        infoLinks: ["Customization guide", "Shipping timeline", "Contact", "Safe packaging"],
        detailIntro: "Each piece is knotted, dried, painted or carved by hand – no two are exactly alike.",
        detailBadge: ["🎨", "100% handcrafted", "Personalized text & photo included"],
        detailNote: "Custom crafted with care",
        aboutTitle: "About this handmade art",
        aboutText: "Keep away from direct excessive heat and harsh chemicals. Dust gently with a dry soft cloth.",
        care: [
            ["⏳", "Lead Time", "Handcrafted to order. Dispatches in 4-5 business days."],
            ["📷", "Customizing", "Upload high-res photos for crisp vibrant printing."],
            ["📦", "Packaging", "Multi-layer bubble wrap ensures 100% break-proof delivery."],
        ],
        categoryIcons: {
            "Macrame Wall Hangings": "🪢",
            "Plant Hangers & Boho Decor": "🌿",
            "Dried Flower Bouquets": "💐",
            "Hand-Painted Pottery": "🏺",
            "Wooden Handicrafts": "🪵",
        },
        occasionsTitle: "Shop by celebration",
        occasionsEyebrow: "💝 Personalized gifting for lifetime memories",
        occasionsSubtitle: "From romantic anniversaries and weddings to heartwarming housewarmings.",
        occasions: [
            ["🪢", "Macrame"], ["🌿", "Planter"], ["💐", "Bouquet"], ["🌹", "Rose"],
            ["🏺", "Vase"], ["🎨", "Painted"], ["🪵", "Wooden"], ["🐴", "Horse"],
        ],
        budgetPrefix: "Gifts under",
        budgets: [599, 1199, 1999],
        completeTitle: "Pair with a dried flower bouquet",
    },
    decor: {
        color: "#3f6b52",
        gradient: ["#3f6b52", "#8fae7e"],
        badgeTop: "Hand poured",
        badgeBottom: "100% natural soy wax",
        icon: "🕯️",
        item: "piece",
        items: "decor pieces",
        heroEyebrow: "SOY CANDLES • CERAMIC VASES • MUGS • RATTAN • DIFFUSERS",
        heroCta: "Shop home sanctuary",
        heroStats: ["🕯️ 100% soy wooden wick", "🏺 Hand-thrown ceramics"],
        searchPlaceholder: "Search candles, vases, mugs, lanterns & reed diffusers",
        searchPrompt: "Find calming ambient scents and artisanal ceramics for your home",
        featuredTitle: "Curated living accents & ambient scents",
        featuredText: "Scented soy candles, hand-thrown ceramic vases and mugs, rattan accents and reed diffusers.",
        readyLine: "pieces ready to elevate your living space.",
        tagline: "Make your home a peaceful sanctuary",
        footerNote: "🕯️ Calm scents and cozy Japandi aesthetics for your home.",
        guestLine: "Cozy scents & aesthetic Nordic decor",
        accountLine: "Your home aesthetic account",
        shopperName: "Home Sanctuary Lover",
        welcome: "Welcome back 🕯️",
        cartTitle: "Your home cart",
        cartEmptyIcon: "🏺",
        checkoutHint: "Tell us where to deliver your home accents.",
        orderDoneLine: "Your home accents are carefully packed and on the way.",
        reviewIntro: "Your feedback on scent throw and quality helps others create their sanctuary.",
        infoTitle: "Candle Care",
        infoLinks: ["Candle burning guide", "Delivery", "Contact", "Gift wrap"],
        detailIntro: "Clean-burning 100% natural soy wax with therapeutic essential oils and crackling wooden wicks.",
        detailBadge: ["🕯️", "100% natural soy wax", "Crackling wooden wick"],
        detailNote: "Phthalate-free essential oil blends",
        aboutTitle: "About this piece",
        aboutText: "Trim wick to 1/4 inch before each burn. Allow wax to melt to the edge of the jar on first burn.",
        care: [
            ["✂️", "Wick Trim", "Trim wick to 1/4 inch before relighting for a clean burn."],
            ["🕯️", "First Burn", "Burn for 2-3 hours so wax pools completely to container edges."],
            ["☕", "Ceramics", "Stoneware mugs are microwave & dishwasher safe."],
        ],
        categoryIcons: {
            "Scented Candles": "🕯️",
            "Ceramic Vases": "🏺",
            "Mugs & Tableware": "☕",
            "Rattan & Boho Decor": "🌿",
            "Reed Diffusers": "✨",
        },
        occasionsTitle: "Shop by room & mood",
        occasionsEyebrow: "✨ Ambient living & aromatherapy",
        occasionsSubtitle: "From cosy scented candles and hand-thrown vases to woven lanterns and reed diffusers.",
        occasions: [
            ["🕯️", "Candle"], ["🏺", "Vase"], ["☕", "Mug"], ["🌿", "Rattan"],
            ["✨", "Diffuser"], ["🌙", "Lantern"], ["🌸", "Floral"], ["🎁", "Set"],
        ],
        budgetPrefix: "Accents under",
        budgets: [499, 899, 1499],
        completeTitle: "Complete the ambiance with a matching diffuser",
    },
    blouses: {
        color: "#9d174d",
        gradient: ["#9d174d", "#d97706"],
        badgeTop: "Handcrafted Luxury",
        badgeBottom: "blouses, sarees & lehengas",
        icon: "🪡",
        item: "blouse",
        items: "designer blouses",
        heroEyebrow: "DESIGNER BLOUSES • SILK SAREES • BRIDAL LEHENGAS",
        heroCta: "Explore boutique collection",
        heroStats: ["🪡 Stitched to your size", "📏 2\" Alteration margin"],
        searchPlaceholder: "Search blouses, silk sarees, lehengas & handloom",
        searchPrompt: "Find the handcrafted designer blouse that elevates your saree",
        featuredTitle: "Trending bridal & designer blouses",
        featuredText: "Designer blouses, silk sarees and bridal lehengas stitched to your measurements.",
        readyLine: "designer blouses ready to wear.",
        tagline: "Grace in every stitch, royal in every thread",
        footerNote: "🪡 Handcrafted with heritage embroidery, tailored to perfection.",
        guestLine: "Designer readymade blouses for your sarees",
        accountLine: "Your boutique studio account",
        shopperName: "Blouse Lover",
        welcome: "Welcome back 🪡",
        cartTitle: "Your wardrobe cart",
        cartEmptyIcon: "👗",
        checkoutHint: "Tell us where to deliver your blouse.",
        orderDoneLine: "Your designer blouse is safely packed and on its way.",
        reviewIntro: "Your feedback on fit and embroidery helps other saree lovers.",
        infoTitle: "Fit & Alterations",
        infoLinks: ["Size chart", "Alteration guide", "Contact", "Returns"],
        detailIntro: "Handcrafted embroidery with pre-sewn cotton lining, molded pads, and 2-inch side margin.",
        detailBadge: ["🪡", "Handcrafted Aari / Maggam", "2\" Side margin included"],
        detailNote: "Pre-stitched with premium cotton lining",
        aboutTitle: "About this blouse",
        aboutText: "Dry clean recommended for heavy zardozi and embroidered blouses. Store wrapped in cotton cloth.",
        care: [
            ["📏", "Alteration", "Includes 2-inch inner side margins to easily expand by 1 size."],
            ["☁️", "Padding", "Comfortable soft molded cups provide structured chest support."],
            ["🧼", "Care", "Dry clean only to preserve metallic zari and stone work."],
        ],
        categoryIcons: {
            "Designer Saree Blouses": "🪡",
            "Silk Sarees with Blouse": "✨",
            "Festive & Party Sarees": "🎉",
            "Bridal Lehengas": "👰",
            "Kerala & Handloom Sarees": "🌿",
        },
        occasionsTitle: "Shop by saree pairing & style",
        occasionsEyebrow: "✨ Royal festive & bridal styling",
        occasionsSubtitle: "From heavy Kanjeevaram wedding silks to Kalamkari cotton and reception velvets.",
        occasions: [
            ["🪡", "Blouse"], ["✨", "Silk"], ["👰", "Lehenga"], ["🌿", "Kerala"],
            ["🔴", "Red"], ["🌸", "Pink"], ["🟢", "Green"], ["🧵", "Handloom"],
        ],
        budgetPrefix: "Blouses under",
        budgets: [1499, 2199, 2999],
        completeTitle: "Complete your festive look",
    },
    uruli: {
        color: "#a16207",
        gradient: ["#a16207", "#eab308"],
        badgeTop: "Handcrafted brass",
        badgeBottom: "authentic temple heritage",
        icon: "🪔",
        item: "artifact",
        items: "brass artifacts",
        heroEyebrow: "BRONZE URULIS • KUTHU VILAKKU • POOJA ESSENTIALS",
        heroCta: "Shop brassware",
        heroStats: ["🪔 Solid bell metal", "📦 Break-proof packing"],
        searchPlaceholder: "Search bronze urulis, kuthu vilakku, pooja thalis & deity idols",
        searchPrompt: "Find sacred handcrafted brassware for your home",
        featuredTitle: "Sacred temple brassware",
        featuredText: "Heirloom Kumbakonam and Mannar bronze artifacts for your sanctum.",
        readyLine: "artifacts ready to bless your home.",
        tagline: "Sacred traditions for modern homes",
        footerNote: "🪔 Authentic handcrafted temple brassware from South Indian artisan clusters.",
        guestLine: "Traditional brass & bronze for home & pooja",
        accountLine: "Your heritage brass account",
        shopperName: "Devotee",
        welcome: "Welcome back 🪔",
        cartTitle: "Your sacred cart",
        cartEmptyIcon: "🪔",
        checkoutHint: "Tell us where to deliver your brass artifacts.",
        orderDoneLine: "Your sacred artifacts are packed in reinforced crates.",
        reviewIntro: "Your feedback on weight, resonance and craftsmanship helps devotees.",
        infoTitle: "Brass Care",
        infoLinks: ["Polishing guide", "Shipping details", "Contact", "Authenticity"],
        detailIntro: "Cast in solid brass and bell-metal bronze by hereditary sthapatis with protective lacquer.",
        detailBadge: ["🪔", "Solid bell metal", "Break-proof shipping"],
        detailNote: "Authentic temple foundry cast",
        aboutTitle: "About this brass artifact",
        aboutText: "Clean gently with tamarind or Pitambari powder. Wipe thoroughly with a soft dry cloth.",
        care: [
            ["✨", "Cleaning", "Polish with Pitambari paste or tamarind pulp to restore gleam."],
            ["💧", "Washing", "Rinse with lukewarm water and dry immediately to prevent water spots."],
            ["📦", "Shipping", "Shipped in multi-layered foam and heavy corrugated boxes."],
        ],
        categoryIcons: {
            "Brass & Copper Urulis": "🌸",
            "Brass Lamps & Vilakku": "🪔",
            "Pooja Thali & Aarti Sets": "✨",
            "Brass Deity Idols": "🏛️",
            "Temple Bells & Diyas": "🔔",
        },
        occasionsTitle: "Shop by sacred occasion",
        occasionsEyebrow: "🪔 Divine blessings",
        occasionsSubtitle: "From Griha Pravesh housewarmings and wedding rituals to daily morning pooja.",
        occasions: [
            ["🌸", "Uruli"], ["🪔", "Vilakku"], ["✨", "Thali"], ["🏛️", "Ganesha"],
            ["🔔", "Bell"], ["🪷", "Diya"], ["🙏", "Krishna"], ["🎁", "Gift"],
        ],
        budgetPrefix: "Artifacts under",
        budgets: [999, 1999, 3999],
        completeTitle: "Complete your pooja setup",
    },
    favors: {
        color: "#db2777",
        gradient: ["#db2777", "#f59e0b"],
        badgeTop: "Bulk tier discounts",
        badgeBottom: "guaranteed on-time delivery",
        icon: "🎀",
        item: "favor",
        items: "return gifts",
        heroEyebrow: "THAMBOOLAM BAGS • FAVOR BOXES • GIFT HAMPERS",
        heroCta: "Shop return gifts",
        heroStats: ["🎁 Tier volume discounts", "📅 Event date delivery"],
        searchPlaceholder: "Search potlis, favor boxes, jars & gift hampers",
        searchPrompt: "Find thoughtful return gifts for your wedding or pooja",
        featuredTitle: "Trending wedding favors",
        featuredText: "Embroidered potlis, ribbon-tied favor boxes and festive hampers for your guests.",
        readyLine: "favors ready to delight your guests.",
        tagline: "Gifts of gratitude & blessing",
        footerNote: "🎀 Thoughtfully curated South Indian return gifts that guests treasure.",
        guestLine: "Curated wedding & ceremony return gifts",
        accountLine: "Your gifting studio account",
        shopperName: "Host",
        welcome: "Welcome back 🎀",
        cartTitle: "Your favor cart",
        cartEmptyIcon: "🎁",
        checkoutHint: "Tell us where and when to deliver your event favors.",
        orderDoneLine: "Your favors are being assembled and tagged for your event.",
        reviewIntro: "Your feedback on guest appreciation helps other event hosts plan.",
        infoTitle: "Bulk Orders",
        infoLinks: ["Tier pricing guide", "Event timelines", "Contact", "Custom tags"],
        detailIntro: "Curated for weddings, housewarmings and baby showers with custom monogram tags.",
        detailBadge: ["🎁", "Bulk discount ready", "Guaranteed event date delivery"],
        detailNote: "Pre-packaged & ready to distribute",
        aboutTitle: "About this favor",
        aboutText: "Supplied individually gift-wrapped in organza or gift boxes with optional name tags.",
        care: [
            ["📅", "Timeline", "Orders ship 7-10 days before your specified event date."],
            ["🎁", "Bulk Tiers", "Automatic discounts apply when selecting larger pack quantities."],
            ["✍️", "Tags", "Personalized with bride & groom names or event thank-you notes."],
        ],
        categoryIcons: {
            "Thamboolam Bags & Potlis": "👜",
            "Wedding Favor Boxes": "🎁",
            "Sweets & Jar Favors": "🍬",
            "Gift Hampers & Baskets": "🧺",
            "Painted Boxes & Festive Gifts": "🪔",
        },
        occasionsTitle: "Shop by event type",
        occasionsEyebrow: "🎀 Memorable guest favors",
        occasionsSubtitle: "From grand South Indian weddings to Seemantham, Upanayanam and housewarmings.",
        occasions: [
            ["👜", "Potli"], ["🎁", "Box"], ["🍬", "Sweets"], ["🧺", "Basket"],
            ["🫙", "Jar"], ["🕯️", "Candle"], ["🪢", "Ribbon"], ["🌿", "Kraft"],
        ],
        budgetPrefix: "Packs under",
        budgets: [1499, 2499, 4999],
        completeTitle: "Pair with another favor",
    },
    resin: {
        color: "#0f766e",
        gradient: ["#0f766e", "#2dd4bf"],
        badgeTop: "Real flowers in resin",
        badgeBottom: "100% crystal epoxy clarity",
        icon: "🌺",
        item: "keepsake",
        items: "floral keepsakes",
        heroEyebrow: "RESIN COASTERS • FLOWER JEWELLERY • PRESSED FRAMES",
        heroCta: "Shop resin art",
        heroStats: ["🌸 Flower pickup available", "💎 Anti-yellowing UV resin"],
        searchPlaceholder: "Search coasters, pendants, earrings, frames & geode art",
        searchPrompt: "Preserve your wedding flowers into permanent art",
        featuredTitle: "Everlasting flower keepsakes",
        featuredText: "Real flowers set in crystal clear resin – coasters, jewellery, frames and wall art.",
        readyLine: "floral keepsakes crafted forever.",
        tagline: "Preserve your sacred blooms",
        footerNote: "🌺 Turning fleeting wedding flowers into permanent, cherished heirloom art.",
        guestLine: "Resin art, flower jewellery & keepsakes",
        accountLine: "Your resin studio account",
        shopperName: "Bride",
        welcome: "Welcome back 🌺",
        cartTitle: "Your preservation cart",
        cartEmptyIcon: "🌸",
        checkoutHint: "Tell us where to collect flowers and ship your keepsake.",
        orderDoneLine: "Our resin artists have received your preservation request.",
        reviewIntro: "Your feedback on clarity and petal vibrancy helps other brides choose.",
        infoTitle: "Preservation Guide",
        infoLinks: ["Flower shipping steps", "Curing timeline", "Contact", "Care guide"],
        detailIntro: "Dehydrated in silica chambers and cast in premium UV-resistant optical clarity epoxy resin.",
        detailBadge: ["🌸", "Varmala preservation", "100% Bubble-free finish"],
        detailNote: "Custom engraved with couple names & date",
        aboutTitle: "About this resin keepsake",
        aboutText: "Keep out of direct sunlight and high heat. Wipe with microfiber cloth and mild water.",
        care: [
            ["🌸", "Flower Prep", "Couriers collect your garland fresh; we dehydrate petals in silica."],
            ["⏳", "Curing", "Requires 3-4 weeks for multi-layer casting and diamond polishing."],
            ["💎", "Resin Care", "UV-stabilized resin will not yellow or discolor over the years."],
        ],
        categoryIcons: {
            "Resin Coasters & Trays": "🌊",
            "Resin Pendants & Necklaces": "💎",
            "Bangles, Earrings & Rings": "💍",
            "Pressed Flower Frames": "🌸",
            "Geode & Ocean Wall Art": "🎨",
        },
        occasionsTitle: "Shop by milestone",
        occasionsEyebrow: "🌺 Sacred preservation",
        occasionsSubtitle: "From coasters and jewellery to pressed flower frames and geode wall art.",
        occasions: [
            ["🌊", "Coaster"], ["💎", "Pendant"], ["💍", "Earring"], ["📿", "Bangle"],
            ["🌸", "Frame"], ["🌹", "Rose"], ["✨", "Gold"], ["🎨", "Geode"],
        ],
        budgetPrefix: "Keepsakes under",
        budgets: [1499, 2499, 3999],
        completeTitle: "Pair with matching coasters",
    },
    crochet: {
        color: "#7c3aed",
        gradient: ["#7c3aed", "#ec4899"],
        badgeTop: "100% hand-crocheted",
        badgeBottom: "never wilts or fades",
        icon: "🧶",
        item: "bloom",
        items: "crochet flowers",
        heroEyebrow: "FOREVER BOUQUETS • CHARMS • PLUSHIES • BAGS",
        heroCta: "Shop crochet",
        heroStats: ["💐 Never-wilting blooms", "🧶 Milk cotton yarn"],
        searchPlaceholder: "Search bouquets, sunflowers, tulips, plushies & bags",
        searchPrompt: "Find everlasting handcrafted crochet flowers and cute plushies",
        featuredTitle: "Trending crochet creations",
        featuredText: "Handcrafted milk cotton yarn flowers and accessories that never fade.",
        readyLine: "crochet blooms crafted with love.",
        tagline: "Flowers that live forever",
        footerNote: "🧶 Hand-knitted with soft cotton yarn, wrapped with love.",
        guestLine: "Everlasting crochet flowers & handmade plushies",
        accountLine: "Your crochet studio account",
        shopperName: "Yarn Lover",
        welcome: "Welcome back 🧶",
        cartTitle: "Your crochet cart",
        cartEmptyIcon: "💐",
        checkoutHint: "Tell us where to deliver your crochet bouquet.",
        orderDoneLine: "Your everlasting flowers are wrapped and on their way.",
        reviewIntro: "Your feedback on yarn quality and wrapping helps fellow gifters.",
        infoTitle: "Crochet Care",
        infoLinks: ["Wrapping options", "Custom bouquets", "Contact", "Gifting"],
        detailIntro: "Hand-looped stitch by stitch from premium soft milk cotton yarn with flexible wire stems.",
        detailBadge: ["🧶", "100% Hand-crocheted", "Never wilts or withers"],
        detailNote: "Korean gift wrapping with satin ribbon",
        aboutTitle: "About this crochet creation",
        aboutText: "Dust gently with a blow dryer on cool setting. Flexible wire stems can be bent to fit any vase.",
        care: [
            ["💐", "No Water", "Never requires water or sunlight; keeps its vibrant color forever."],
            ["✨", "Wire Stems", "Bend or trim stems easily to arrange in different vases."],
            ["🎁", "Packaging", "Shipped in sturdy boxes so flowers arrive without crushing."],
        ],
        categoryIcons: {
            "Crochet Flower Bouquets": "💐",
            "Single Stems & Potted Blooms": "🌷",
            "Keychains, Charms & Bookmarks": "🔑",
            "Amigurumi Plushies": "🧸",
            "Crochet Bags": "👜",
        },
        occasionsTitle: "Shop by gifting moment",
        occasionsEyebrow: "🧶 Everlasting gifts",
        occasionsSubtitle: "From graduations and anniversaries to birthday surprises and car aesthetics.",
        occasions: [
            ["💐", "Bouquet"], ["🌻", "Sunflower"], ["🌷", "Tulip"], ["🌹", "Rose"],
            ["🔑", "Keychain"], ["📖", "Bookmark"], ["🧸", "Plush"], ["👜", "Bag"],
        ],
        budgetPrefix: "Creations under",
        budgets: [499, 999, 1499],
        completeTitle: "Build your dream bouquet",
    },
    pattu: {
        color: "#c026d3",
        gradient: ["#c026d3", "#fb7185"],
        badgeTop: "100% skin-friendly",
        badgeBottom: "cotton lined comfort",
        icon: "🧒",
        item: "outfit",
        items: "festive ethnic wear",
        heroEyebrow: "PATTU PAVADAI • LEHENGAS • BOYS KURTA SETS",
        heroCta: "Shop kids ethnic",
        heroStats: ["☁️ Soft pure cotton lining", "👶 Newborn 0M to 12 Years"],
        searchPlaceholder: "Search pavadai, lehengas, kurta sets & sibling sets",
        searchPrompt: "Find handcrafted festive ethnic wear for boys, girls and babies",
        featuredTitle: "Trending festive collections",
        featuredText: "Traditional South Indian weaves tailored with child-safe comfort and soft linings.",
        readyLine: "festive outfits tailored for your little ones.",
        tagline: "Pure tradition, gentle comfort",
        footerNote: "🧒 Tailored with pure cotton lining for happy, smiling celebrations.",
        guestLine: "Kids pattu pavadai & traditional festive wear",
        accountLine: "Your kids boutique account",
        shopperName: "Parent",
        welcome: "Welcome back 🧒",
        cartTitle: "Your festive wardrobe",
        cartEmptyIcon: "🥻",
        checkoutHint: "Tell us where to deliver your kid's celebration outfit.",
        orderDoneLine: "Your festive attire is neatly packed and dispatched.",
        reviewIntro: "Your review on sizing, fabric softness and stitching helps other parents.",
        infoTitle: "Kids Sizing & Care",
        infoLinks: ["Age size chart", "Alteration margins", "Contact", "Fabric care"],
        detailIntro: "Handcrafted festive silken weaves lined with 100% combed skin-friendly breathable cotton.",
        detailBadge: ["🧒", "Pure cotton lining", "Kid-safe soft seams"],
        detailNote: "Adjustable waist ties with extra margin",
        aboutTitle: "About this ethnic outfit",
        aboutText: "Dry clean first wash. Subsequent washes gentle hand wash in cold water with mild baby detergent. Do not wring.",
        care: [
            ["☁️", "Lining", "Pure combed cotton inner lining prevents rashes and itching."],
            ["📏", "Alteration", "Includes 2 inches side seam allowance to adjust as your child grows."],
            ["🧼", "Washing", "Cold gentle hand wash with mild baby soap; dry in shade."],
        ],
        categoryIcons: {
            "Pattu Pavadai & Silk Skirts": "👗",
            "Lehenga Cholis": "💃",
            "Boys Kurta & Dhoti Sets": "🤵",
            "Half Sarees & Festive Wear": "✨",
            "Little Ones & Sibling Sets": "👶",
        },
        occasionsTitle: "Shop by celebration",
        occasionsEyebrow: "👑 Precious moments",
        occasionsSubtitle: "From naming ceremonies and first birthdays to Diwali and temple weddings.",
        occasions: [
            ["👗", "Pavadai"], ["💃", "Lehenga"], ["🤵", "Kurta"], ["🧥", "Jacket"],
            ["✨", "Saree"], ["🦚", "Krishna"], ["🌼", "Onam"], ["👶", "Baby"],
        ],
        budgetPrefix: "Outfits under",
        budgets: [999, 1799, 2499],
        completeTitle: "Pair with matching accessories",
    },
    linens: {
        color: "#0369a1",
        gradient: ["#0369a1", "#06b6d4"],
        badgeTop: "100% pure cotton",
        badgeBottom: "handblock printed",
        icon: "🛏️",
        item: "bedsheet",
        items: "home textiles",
        heroEyebrow: "COTTON BEDSHEETS • QUILTS • CUSHIONS • RUGS",
        heroCta: "Shop bed & living",
        heroStats: ["🌿 300 Thread Count Cotton", "👑 True 108x108\" King Size"],
        searchPlaceholder: "Search bedsheets, quilts, cushion covers & rugs",
        searchPrompt: "Find authentic Rajasthani handblock bedding and living decor",
        featuredTitle: "Handblock bedding & living",
        featuredText: "Breathable pure combed cotton sheets and feather-light reversible mulmul AC dohars.",
        readyLine: "artisan home linens crafted for comfortable living.",
        tagline: "Natural comfort for restful spaces",
        footerNote: "🛏️ Handblock printed with eco-friendly dyes on 100% combed cotton.",
        guestLine: "Handblock bedsheets, quilts & living decor",
        accountLine: "Your home textiles account",
        shopperName: "Home Lover",
        welcome: "Welcome back 🛏️",
        cartTitle: "Your bedding cart",
        cartEmptyIcon: "🛏️",
        checkoutHint: "Tell us where to deliver your handcrafted home linens.",
        orderDoneLine: "Your cotton bedsheets and dohars are on their way.",
        reviewIntro: "Your feedback on fabric softness, deep tuck fit and print sharpness helps home lovers.",
        infoTitle: "Linen Care",
        infoLinks: ["Mattress size guide", "Thread count info", "Contact", "Wash guide"],
        detailIntro: "Hand-pressed with wooden blocks by master artisans on breathable 100% combed long-staple cotton.",
        detailBadge: ["🛏️", "100% Combed Cotton", "Deep 108x108\" King Fit"],
        detailNote: "Includes 2 coordinated envelope pillow shams",
        aboutTitle: "About this artisan textile",
        aboutText: "Machine wash cold on gentle cycle with mild detergent. Wash dark colors separately first. Line dry in shade.",
        care: [
            ["📏", "Deep Fit", "Generous 108x108\" size easily tucks under thick 8-10\" mattresses."],
            ["🎨", "Natural Dyes", "Natural vegetable and azo-free reactive dyes that stay vibrant."],
            ["🧼", "Wash Care", "Machine wash cold gentle; gets noticeably softer with each wash."],
        ],
        categoryIcons: {
            "Printed Cotton Bedsheets": "🛏️",
            "Quilts, Dohars & Throws": "🧶",
            "Cushion Covers": "🛋️",
            "Durries & Rugs": "🧺",
            "Block Print Fabrics": "🌿",
        },
        occasionsTitle: "Shop by room & living",
        occasionsEyebrow: "🌿 Earthy bedroom styling",
        occasionsSubtitle: "From master bedroom king setups to cozy guest rooms and living room accents.",
        occasions: [
            ["🛏️", "Bedsheet"], ["🌸", "Floral"], ["🧶", "Quilt"], ["🎨", "Patchwork"],
            ["🛋️", "Cushion"], ["🧺", "Rug"], ["🌿", "Block Print"], ["🔵", "Indigo"],
        ],
        budgetPrefix: "Linens under",
        budgets: [999, 1699, 2499],
        completeTitle: "Complete your bedroom look",
    },
    tshirts: {
        color: "#7c3aed",
        gradient: ["#7c3aed", "#ec4899"],
        badgeTop: "Your design",
        badgeBottom: "printed on soft cotton",
        icon: "👕",
        item: "T-shirt",
        items: "custom T-shirts",
        heroEyebrow: "MEN • WOMEN • BOYS • GIRLS • COUPLE TEES",
        heroCta: "Design your tee",
        heroStats: ["🎨 Live design preview", "👕 180 GSM cotton"],
        searchPlaceholder: "Search name tees, photo tees, couple & birthday T-shirts",
        searchPrompt: "Find a design or print your own photo",
        featuredTitle: "Trending custom tees",
        featuredText: "Typography, photo and graphic tees you can personalise with your own name or photo.",
        readyLine: "custom T-shirts ready to print for you.",
        tagline: "Custom printed T-shirts for everyone",
        footerNote: "👕 Vivid wash-proof prints on soft 180 GSM combed cotton.",
        guestLine: "Custom printed tees for the whole family",
        accountLine: "Your custom tee studio account",
        shopperName: "Tee Designer",
        welcome: "Welcome back 👕",
        cartTitle: "Your tee bag",
        cartEmptyIcon: "👕",
        checkoutHint: "Tell us where to deliver your custom T-shirts.",
        orderDoneLine: "Your custom T-shirts are being printed and packed.",
        reviewIntro: "Your feedback on fit, fabric and print quality helps other shoppers choose.",
        infoTitle: "Print & Fit",
        infoLinks: ["Size chart", "Print guide", "Contact", "Wash care"],
        detailIntro: "Soft 180 GSM combed cotton with a vivid, crack-resistant print that stays bright wash after wash.",
        detailBadge: ["🎨", "Your design", "Live preview before you order"],
        detailNote: "Printed fresh for your order",
        aboutTitle: "About this T-shirt",
        aboutText: "Machine wash cold inside out. Do not iron directly on the print. Dry in shade to keep colours bright.",
        care: [
            ["👕", "180 GSM Cotton", "Breathable combed cotton in a true-to-size regular fit."],
            ["🌈", "Wash-Proof Print", "Vivid colours that do not crack or fade quickly."],
            ["🎨", "Live Preview", "See your name, text or photo on the tee before ordering."],
        ],
        categoryIcons: { Men: "👨", Women: "👩", Boys: "👦", Girls: "👧", "Couple & Family": "💑" },
        occasionsTitle: "Shop by occasion",
        occasionsEyebrow: "🎉 Tees for every moment",
        occasionsSubtitle: "Birthdays, trips, weddings or everyday style: find a tee or print your own.",
        occasions: [
            ["👨", "Men"], ["👩", "Women"], ["👦", "Boys"], ["👧", "Girls"],
            ["💑", "Couple"], ["👨‍👩‍👧", "Family"], ["🎂", "Birthday"], ["✍️", "Quote"],
        ],
        budgetPrefix: "Tees under",
        budgets: [449, 549, 699],
        completeTitle: "Make it a matching set",
    },
    acrylic: {
        color: "#0e7490",
        gradient: ["#0e7490", "#22d3ee"],
        badgeTop: "Crystal-clear",
        badgeBottom: "acrylic HD prints",
        icon: "🖼️",
        item: "acrylic print",
        items: "acrylic photos & wall art",
        heroEyebrow: "ACRYLIC PHOTOS • WALL ART • CLOCKS • COLLAGES",
        heroCta: "Create your acrylic",
        heroStats: ["✨ 5 mm glossy acrylic", "🔩 Ready-to-hang mounts"],
        searchPlaceholder: "Search acrylic photo frames, wall clocks, collages & wall art",
        searchPrompt: "Turn your favourite photo into glossy wall decor",
        featuredTitle: "Bestselling acrylic photos",
        featuredText: "Glossy acrylic frames, clocks and wall art printed in vivid HD colour.",
        readyLine: "acrylic prints ready to hang on your wall.",
        tagline: "Your photos on crystal-clear acrylic",
        footerNote: "🖼️ HD prints on 5 mm acrylic with steel wall mounts.",
        guestLine: "Acrylic photo frames, clocks & wall art",
        accountLine: "Your acrylic studio account",
        shopperName: "Memory Keeper",
        welcome: "Welcome back 🖼️",
        cartTitle: "Your acrylic cart",
        cartEmptyIcon: "🖼️",
        checkoutHint: "Tell us where to deliver your acrylic prints.",
        orderDoneLine: "Your acrylic prints are being made and packed safely.",
        reviewIntro: "Your feedback on print clarity and finish helps other shoppers choose.",
        infoTitle: "Acrylic Guide",
        infoLinks: ["Size guide", "Photo tips", "Contact", "Hanging guide"],
        detailIntro: "5 mm crystal-clear acrylic with a glossy HD print, polished edges and steel standoff mounts.",
        detailBadge: ["✨", "Crystal-clear acrylic", "Live photo preview"],
        detailNote: "Wall mounting kit included",
        aboutTitle: "About this acrylic print",
        aboutText: "Wipe gently with a soft microfibre cloth. Avoid harsh cleaners and direct sunlight for long-lasting colour.",
        care: [
            ["✨", "Glossy HD Print", "Vivid colours with a glass-like shine and depth."],
            ["🔩", "Ready to Hang", "Steel standoff mounts and screws included."],
            ["📦", "Break-Safe Packing", "Foam-wrapped and boxed for safe delivery."],
        ],
        categoryIcons: {
            "Acrylic Photo Frames": "📸", "Wall Paintings & Art": "🎨", "Acrylic Wall Clocks": "🕰️", "Photo Collages": "🧩", "Tabletop & Nameplates": "🏷️",
        },
        occasionsTitle: "Shop by style",
        occasionsEyebrow: "🖼️ Decor that tells your story",
        occasionsSubtitle: "Wedding portraits, pet photos, wall art and gifts for every occasion.",
        occasions: [
            ["🖼️", "Photo Frame"], ["🎨", "Abstract"], ["🕰️", "Clock"], ["🧩", "Collage"],
            ["🖤", "Black"], ["🤍", "White"], ["🏠", "Gallery Wall"], ["🗂️", "Tabletop"],
        ],
        budgetPrefix: "Acrylic under",
        budgets: [999, 1499, 1999],
        completeTitle: "Complete your photo wall",
    },
    beauty: {
        color: "#db2777",
        gradient: ["#db2777", "#a855f7"],
        badgeTop: "New season",
        badgeBottom: "fashion & beauty",
        icon: "💖",
        item: "product",
        items: "fashion & beauty picks",
        heroEyebrow: "WESTERN • ETHNIC • MAKEUP • SKINCARE • FRAGRANCE",
        heroCta: "Shop the edit",
        heroStats: ["✨ New drops weekly", "🔁 Easy exchanges"],
        searchPlaceholder: "Search dresses, kurtis, lipsticks, perfumes & more",
        searchPrompt: "Find your next favourite outfit or beauty pick",
        featuredTitle: "Trending this week",
        featuredText: "Outfits, makeup and fragrances our shoppers love right now.",
        readyLine: "fashion & beauty picks ready to ship.",
        tagline: "Fashion and beauty in one place",
        footerNote: "💖 Curated fashion, makeup, skincare and fragrance.",
        guestLine: "Fashion, makeup & skincare in one cart",
        accountLine: "Your style & beauty account",
        shopperName: "Style Icon",
        welcome: "Welcome back 💖",
        cartTitle: "Your style bag",
        cartEmptyIcon: "👜",
        checkoutHint: "Tell us where to deliver your fashion & beauty picks.",
        orderDoneLine: "Your outfits and beauty picks are being packed.",
        reviewIntro: "Your feedback on fit, shade and quality helps other shoppers choose.",
        infoTitle: "Shop Help",
        infoLinks: ["Size guide", "Shade guide", "Contact", "Exchanges"],
        detailIntro: "Hand-picked for quality, comfort and a look you will love.",
        detailBadge: ["💖", "Curated quality", "Checked before dispatch"],
        detailNote: "Easy 7-day exchange",
        aboutTitle: "About this product",
        aboutText: "Store in a cool, dry place. For clothing, wash gently in cold water and dry in shade.",
        care: [["👗", "Fashion", "Comfortable fabrics in flattering, true-to-size fits."], ["💄", "Beauty", "Skin-safe, long-wear makeup and gentle skincare."], ["🔁", "Exchanges", "Size or shade not right? Exchange within 7 days."]],
        categoryIcons: {"Western Wear": "👗", "Ethnic Wear": "🥻", "Makeup": "💄", "Skincare": "🧴", "Fragrances": "🌸"},
        occasionsTitle: "Shop by look",
        occasionsEyebrow: "✨ Complete your look",
        occasionsSubtitle: "From brunch outfits to festive glam, find everything for your look.",
        occasions: [["👗", "Dress"], ["🧥", "Co-ord"], ["🥻", "Saree"], ["💃", "Lehenga"], ["💄", "Lipstick"], ["🎨", "Palette"], ["🌸", "Perfume"], ["🧴", "Skincare"]],
        budgetPrefix: "Picks under",
        budgets: [499, 999, 1999],
        completeTitle: "Complete your look",
    },
    cosmetics: {
        color: "#be185d",
        gradient: ["#be185d", "#f472b6"],
        badgeTop: "Cruelty-free",
        badgeBottom: "shades for every skin",
        icon: "💄",
        item: "product",
        items: "makeup & skincare",
        heroEyebrow: "LIPS • FACE • EYES • NAILS • SKINCARE",
        heroCta: "Shop makeup",
        heroStats: ["🐰 Cruelty-free", "🎨 Shades for every tone"],
        searchPlaceholder: "Search lipsticks, foundations, palettes, nail polish & serums",
        searchPrompt: "Find your perfect shade",
        featuredTitle: "Bestselling makeup",
        featuredText: "Long-wear lips, glowing skin and bold eyes, loved by thousands.",
        readyLine: "beauty favourites ready to ship.",
        tagline: "Makeup made for every skin tone",
        footerNote: "💄 Cruelty-free, long-wear makeup and skincare.",
        guestLine: "Lipsticks, palettes, skincare & more",
        accountLine: "Your beauty account",
        shopperName: "Beauty Lover",
        welcome: "Welcome back 💄",
        cartTitle: "Your beauty bag",
        cartEmptyIcon: "💄",
        checkoutHint: "Tell us where to deliver your beauty picks.",
        orderDoneLine: "Your makeup is being packed with care.",
        reviewIntro: "Your feedback on shade, texture and wear time helps other shoppers find their match.",
        infoTitle: "Beauty Help",
        infoLinks: ["Shade finder", "Ingredients", "Contact", "Returns"],
        detailIntro: "Highly pigmented, long-wear formula that is cruelty-free and gentle on skin.",
        detailBadge: ["🐰", "Cruelty-free", "Dermatologist tested"],
        detailNote: "Sealed & tamper-proof packing",
        aboutTitle: "About this product",
        aboutText: "Patch test before first use. Store away from direct sunlight with the cap closed.",
        care: [["🐰", "Cruelty-Free", "Never tested on animals."], ["🎨", "Every Skin Tone", "Shades made for Indian skin tones."], ["⏱️", "Long-Wear", "Colour that lasts from day to night."]],
        categoryIcons: {"Lips": "💄", "Face": "✨", "Eyes": "👁️", "Nails": "💅", "Skincare & Sets": "🧴"},
        occasionsTitle: "Shop by product",
        occasionsEyebrow: "💄 Find your favourites",
        occasionsSubtitle: "Lipsticks, palettes, polish and serums in every shade.",
        occasions: [["💄", "Lipstick"], ["✨", "Gloss"], ["🧴", "Foundation"], ["🌸", "Blush"], ["🎨", "Palette"], ["👁️", "Mascara"], ["💅", "Polish"], ["💧", "Serum"]],
        budgetPrefix: "Beauty under",
        budgets: [299, 499, 799],
        completeTitle: "Complete your look",
    },
    kurti: {
        color: "#c026d3",
        gradient: ["#c026d3", "#f59e0b"],
        badgeTop: "Stitched",
        badgeBottom: "to your size",
        icon: "🧵",
        item: "kurti",
        items: "custom kurtis",
        heroEyebrow: "COTTON • CHIKANKARI • KURTA SETS • FESTIVE • DESIGN YOUR OWN",
        heroCta: "Design your kurti",
        heroStats: ["📏 Stitched to your size", "🔁 Free alterations"],
        searchPlaceholder: "Search cotton kurtis, chikankari, kurta sets & festive kurtis",
        searchPrompt: "Find a design and make it yours",
        featuredTitle: "Most-loved kurtis",
        featuredText: "Pick a design, choose colour, neck and sleeves, and we stitch it to your size.",
        readyLine: "kurtis ready to stitch for you.",
        tagline: "Kurtis made just for you",
        footerNote: "🧵 Made to measure by skilled tailors and artisans.",
        guestLine: "Custom kurtis stitched to your size",
        accountLine: "Your kurti studio account",
        shopperName: "Style Queen",
        welcome: "Welcome back 🧵",
        cartTitle: "Your kurti bag",
        cartEmptyIcon: "👗",
        checkoutHint: "Tell us where to deliver your custom kurtis.",
        orderDoneLine: "Your kurtis are being cut and stitched to your size.",
        reviewIntro: "Your feedback on fit, fabric and stitching helps other shoppers choose.",
        infoTitle: "Fit & Stitching",
        infoLinks: ["How to measure", "Size chart", "Contact", "Alterations"],
        detailIntro: "Stitched to your measurements in breathable fabric, with your choice of colour, neck, sleeves and length.",
        detailBadge: ["📏", "Made to measure", "Stitched in 5 to 7 days"],
        detailNote: "Free alteration within 7 days",
        aboutTitle: "About this kurti",
        aboutText: "Hand wash or gentle machine wash cold. Dry in shade and iron on medium heat inside out.",
        care: [["📏", "Your Size", "Stitched to your measurements or standard sizes."], ["🎨", "Your Style", "Choose colour, neck, sleeves and length."], ["🔁", "Free Alterations", "We fix the fit for free within 7 days."]],
        categoryIcons: {"Cotton Kurtis": "🌿", "Chikankari & Embroidered": "🪡", "Kurta Sets": "👗", "Festive & Party Kurtis": "✨", "Design Your Own Kurti": "✂️"},
        occasionsTitle: "Shop by style",
        occasionsEyebrow: "🧵 Made to your measure",
        occasionsSubtitle: "Everyday cotton, chikankari, festive and design-your-own kurtis.",
        occasions: [["🌿", "Cotton"], ["🪡", "Chikankari"], ["🧵", "Block Print"], ["✨", "Festive"], ["👗", "Kurta Set"], ["✂️", "Custom"], ["💛", "Zari"], ["🌸", "Floral"]],
        budgetPrefix: "Kurtis under",
        budgets: [699, 999, 1499],
        completeTitle: "Complete your ethnic look",
    },
    perfume: {
        color: "#7c3aed",
        gradient: ["#7c3aed", "#ec4899"],
        badgeTop: "Long-lasting",
        badgeBottom: "scents & name soaps",
        icon: "🌸",
        item: "fragrance",
        items: "perfumes, attars & soaps",
        heroEyebrow: "MEN • WOMEN • ATTARS • NAME SOAPS • GIFT SETS",
        heroCta: "Find your scent",
        heroStats: ["⏳ Long-lasting", "🧼 Name-engraved soaps"],
        searchPlaceholder: "Search oud, rose, attar, name soap & gift sets",
        searchPrompt: "Find a signature scent or a soap with your name",
        featuredTitle: "Bestselling scents",
        featuredText: "Perfumes for him and her, pure attars and handmade soaps with your name.",
        readyLine: "fragrances ready to gift.",
        tagline: "Scents that tell your story",
        footerNote: "🌸 Long-lasting perfumes, pure attars and handmade name soaps.",
        guestLine: "Perfumes, attars & custom soaps",
        accountLine: "Your fragrance account",
        shopperName: "Scent Lover",
        welcome: "Welcome back 🌸",
        cartTitle: "Your scent bag",
        cartEmptyIcon: "🌸",
        checkoutHint: "Tell us where to deliver your fragrances.",
        orderDoneLine: "Your perfumes and soaps are being packed with care.",
        reviewIntro: "Your feedback on longevity and scent helps other shoppers choose.",
        infoTitle: "Fragrance Help",
        infoLinks: ["Scent guide", "How to apply", "Contact", "Gift wrapping"],
        detailIntro: "Long-lasting fragrance, pure attar oils and handmade soaps, packed gift-ready.",
        detailBadge: ["🌸", "Long-lasting", "Gift wrapping available"],
        detailNote: "Sealed and leak-proof packing",
        aboutTitle: "About this fragrance",
        aboutText: "Spray on pulse points: wrists, neck and behind the ears. Store away from sunlight. Soaps: keep dry between uses.",
        care: [["⏳", "Long-Lasting", "Rich concentrations that last through the day."], ["🌿", "Pure Attars", "Alcohol-free attar oils made the traditional way."], ["🧼", "Name Soaps", "Handmade soaps engraved with any name or message."]],
        categoryIcons: {"Men's Perfumes": "🕴️", "Women's Perfumes": "💐", "Attars": "🪔", "Customised Soaps": "🧼", "Gift Sets": "🎁"},
        occasionsTitle: "Shop by scent",
        occasionsEyebrow: "🌸 Find your signature",
        occasionsSubtitle: "Woody, fresh, floral or sweet: find a scent that feels like you.",
        occasions: [["🪵", "Oud"], ["🌹", "Rose"], ["🤍", "Musk"], ["🍋", "Citrus"], ["🌼", "Jasmine"], ["🍦", "Vanilla"], ["🪔", "Attar"], ["🧼", "Soap"]],
        budgetPrefix: "Scents under",
        budgets: [299, 999, 1499],
        completeTitle: "Make it a gift set",
    },
    toys: {
        color: "#f97316",
        gradient: ["#f97316", "#facc15"],
        badgeTop: "Wholesale",
        badgeBottom: "factory prices",
        icon: "🧸",
        item: "toy",
        items: "toys",
        heroEyebrow: "SOFT TOYS • CARS • PUZZLES • DOLLS • RIDE-ONS",
        heroCta: "Shop wholesale toys",
        heroStats: ["🏭 Factory prices", "📦 Packs of 6 to 48"],
        searchPlaceholder: "Search teddy, cars, blocks, dolls & ride-ons",
        searchPrompt: "Find toys for your shop in bulk packs",
        featuredTitle: "Bestselling wholesale toys",
        featuredText: "Soft toys, die-cast cars, learning toys and ride-ons in wholesale packs.",
        readyLine: "toys ready for your shelves.",
        tagline: "Wholesale toys for every shop",
        footerNote: "🧸 Wholesale toys with GST bills and pan-India transport.",
        guestLine: "Wholesale toys for shops",
        accountLine: "Your wholesale account",
        shopperName: "Retailer",
        welcome: "Welcome back 🧸",
        cartTitle: "Your wholesale order",
        cartEmptyIcon: "🧸",
        checkoutHint: "Tell us where to send your toy order.",
        orderDoneLine: "Your toys are being packed for dispatch.",
        reviewIntro: "Your feedback on quality and packing helps other retailers.",
        infoTitle: "Wholesale Help",
        infoLinks: ["Pack sizes", "Transport", "Contact", "GST billing"],
        detailIntro: "Priced per piece; choose a wholesale pack. Mixed designs in every pack.",
        detailBadge: ["📦", "Wholesale packs", "GST bill on every order"],
        detailNote: "Transport to any city in India",
        aboutTitle: "About this toy",
        aboutText: "Prices shown are per piece in the selected pack. Carton orders get extra discounts.",
        care: [["🏭", "Factory Direct", "Better margins for your shop."], ["🧒", "Child Safe", "Non-toxic materials and smooth edges."], ["🚚", "Pan-India", "Road transport to any city."]],
        categoryIcons: {"Soft Toys & Teddies": "🧸", "Cars & Vehicles": "🚗", "Educational & Puzzles": "🧩", "Dolls & Handmade Toys": "🪆", "Ride-ons & Outdoor": "🛴"},
        occasionsTitle: "Shop by toy type",
        occasionsEyebrow: "🧸 For every age",
        occasionsSubtitle: "Teddies and cars to puzzles and ride-ons for your toy shop.",
        occasions: [["🧸", "Teddy"], ["🚗", "Car"], ["🧱", "Blocks"], ["🧩", "Puzzle"], ["🪆", "Doll"], ["🦕", "Dinosaur"], ["🚂", "Train"], ["🛴", "Ride-on"]],
        budgetPrefix: "Toys under",
        budgets: [149, 499, 1499],
        completeTitle: "Add more to your carton",
    },
    medcare: {
        color: "#0284c7",
        gradient: ["#0284c7", "#14b8a6"],
        badgeTop: "100% genuine",
        badgeBottom: "health products",
        icon: "🩺",
        item: "product",
        items: "health products",
        heroEyebrow: "MONITORS • FIRST AID • VITAMINS • MOBILITY",
        heroCta: "Shop health essentials",
        heroStats: ["✅ Genuine products", "🚚 Fast delivery"],
        searchPlaceholder: "Search BP monitor, oximeter, first aid & vitamins",
        searchPrompt: "Find genuine health products for your family",
        featuredTitle: "Most trusted health products",
        featuredText: "BP monitors, oximeters, first aid, vitamins and mobility aids.",
        readyLine: "health products ready to ship.",
        tagline: "Care for your family's health",
        footerNote: "🩺 Genuine health products. Consult your doctor before use.",
        guestLine: "Health monitors & medical supplies",
        accountLine: "Your health account",
        shopperName: "Customer",
        welcome: "Welcome back 🩺",
        cartTitle: "Your health cart",
        cartEmptyIcon: "🩺",
        checkoutHint: "Tell us where to deliver your health products.",
        orderDoneLine: "Your order is being packed with care.",
        reviewIntro: "Your feedback helps other families choose the right product.",
        infoTitle: "Health Help",
        infoLinks: ["How to use", "Warranty", "Contact", "Returns"],
        detailIntro: "Genuine product from authorised distributors, with warranty where applicable.",
        detailBadge: ["✅", "100% genuine", "Warranty on devices"],
        detailNote: "Consult your doctor before use",
        aboutTitle: "About this product",
        aboutText: "Read the instructions before use. Devices come with the brand's warranty card.",
        care: [["✅", "Genuine", "From authorised distributors."], ["🛡️", "Warranty", "Warranty on health devices."], ["📞", "Support", "Help choosing the right product."]],
        categoryIcons: {"Health Monitors": "🩺", "First Aid": "🩹", "Vitamins & Supplements": "💊", "Hygiene & Protection": "🧴", "Mobility & Support": "🦯"},
        occasionsTitle: "Shop by need",
        occasionsEyebrow: "🩺 Health at home",
        occasionsSubtitle: "Monitors, first aid, vitamins and mobility aids for every family.",
        occasions: [["🩺", "BP Monitor"], ["🫁", "Oximeter"], ["🌡️", "Thermometer"], ["🩹", "First Aid"], ["💊", "Vitamin"], ["🧴", "Sanitizer"], ["😷", "Mask"], ["🦯", "Walker"]],
        budgetPrefix: "Under",
        budgets: [299, 999, 1999],
        completeTitle: "Complete your home health kit",
    },
    dryfruits: {
        color: "#b45309",
        gradient: ["#b45309", "#f59e0b"],
        badgeTop: "Premium grade",
        badgeBottom: "freshly packed",
        icon: "🥜",
        item: "pack",
        items: "dry fruits",
        heroEyebrow: "CASHEWS • ALMONDS • PISTACHIOS • DATES • GIFT BOXES",
        heroCta: "Shop dry fruits",
        heroStats: ["🌰 Premium grades", "🎁 Festive gift boxes"],
        searchPlaceholder: "Search cashews, almonds, dates, figs & gift boxes",
        searchPrompt: "Find premium dry fruits and gift boxes",
        featuredTitle: "Bestselling dry fruits",
        featuredText: "Cashews, almonds, pistachios, dates and festive gift boxes.",
        readyLine: "dry fruits packed fresh.",
        tagline: "Premium dry fruits, freshly packed",
        footerNote: "🥜 Premium dry fruits packed fresh every week.",
        guestLine: "Dry fruits & gift boxes",
        accountLine: "Your dry fruit account",
        shopperName: "Nut Lover",
        welcome: "Welcome back 🥜",
        cartTitle: "Your dry fruit basket",
        cartEmptyIcon: "🥜",
        checkoutHint: "Tell us where to deliver your dry fruits.",
        orderDoneLine: "Your dry fruits are being packed fresh.",
        reviewIntro: "Your feedback on taste and freshness helps other shoppers.",
        infoTitle: "Dry Fruit Help",
        infoLinks: ["Storage tips", "Bulk orders", "Contact", "Gift boxes"],
        detailIntro: "Premium grade, packed fresh in airtight zip pouches.",
        detailBadge: ["🌰", "Premium grade", "Packed fresh weekly"],
        detailNote: "Store in an airtight jar in a cool place",
        aboutTitle: "About these dry fruits",
        aboutText: "Store in an airtight container away from heat. Refrigerate in summer for longer freshness.",
        care: [["🌰", "Premium", "Hand-sorted top grades."], ["📦", "Fresh", "Packed in small batches."], ["🎁", "Gifting", "Boxes for every festival."]],
        categoryIcons: {"Almonds & Cashews": "🌰", "Pistachios & Walnuts": "🥜", "Dates & Figs": "🌴", "Raisins & Dried Fruits": "🍇", "Mixes & Gift Boxes": "🎁"},
        occasionsTitle: "Shop by favourite",
        occasionsEyebrow: "🥜 Healthy snacking",
        occasionsSubtitle: "Nuts, dates and dried fruits for snacks, sweets and gifting.",
        occasions: [["🌰", "Cashew"], ["🥜", "Almond"], ["💚", "Pistachio"], ["🧠", "Walnut"], ["🌴", "Dates"], ["🍇", "Raisins"], ["🍑", "Apricot"], ["🎁", "Gift"]],
        budgetPrefix: "Under",
        budgets: [299, 599, 999],
        completeTitle: "Make it a gift box",
    },
    bedbath: {
        color: "#0369a1",
        gradient: ["#0369a1", "#38bdf8"],
        badgeTop: "100% cotton",
        badgeBottom: "hotel quality",
        icon: "🛁",
        item: "piece",
        items: "towels & linen",
        heroEyebrow: "BATH TOWELS • BEDSHEETS • PILLOWS • BATHROBES",
        heroCta: "Shop towels & linen",
        heroStats: ["🧺 100% cotton", "🏨 Hotel quality"],
        searchPlaceholder: "Search towels, bedsheets, pillows & bathrobes",
        searchPrompt: "Find soft towels and bed linen",
        featuredTitle: "Bestselling towels & linen",
        featuredText: "Plush towels, cotton bedsheets, pillows and bathrobes.",
        readyLine: "towels and linen ready to ship.",
        tagline: "Softness for every home",
        footerNote: "🛁 100% cotton towels and bed linen.",
        guestLine: "Towels, bedsheets & bathrobes",
        accountLine: "Your SoftNest account",
        shopperName: "Home Lover",
        welcome: "Welcome back 🛁",
        cartTitle: "Your linen basket",
        cartEmptyIcon: "🛁",
        checkoutHint: "Tell us where to deliver your towels and linen.",
        orderDoneLine: "Your towels and linen are being packed.",
        reviewIntro: "Your feedback on softness and colour helps other shoppers.",
        infoTitle: "Care & Sizes",
        infoLinks: ["Size guide", "Washing care", "Contact", "Bulk orders"],
        detailIntro: "100% cotton, absorbent and soft, with colours that stay bright.",
        detailBadge: ["🧺", "100% cotton", "Fast colours"],
        detailNote: "Machine washable",
        aboutTitle: "About this product",
        aboutText: "Wash before first use. Machine wash cold with similar colours; tumble dry low.",
        care: [["🧺", "Cotton", "Soft and absorbent."], ["🎨", "Fast Colours", "Stay bright wash after wash."], ["🏨", "Bulk", "Rates for hotels and spas."]],
        categoryIcons: {"Bath Towels": "🛁", "Hand & Face Towels": "🧼", "Bedsheets": "🛏️", "Pillows & Comforters": "☁️", "Bathrobes & Beach Towels": "🥋"},
        occasionsTitle: "Shop by room",
        occasionsEyebrow: "🛁 Bath & bedroom",
        occasionsSubtitle: "Towels, bedsheets, pillows and bathrobes for your home or hotel.",
        occasions: [["🛁", "Bath Towel"], ["🧼", "Hand Towel"], ["🛏️", "Bedsheet"], ["☁️", "Pillow"], ["🧸", "Comforter"], ["🥋", "Bathrobe"], ["🏖️", "Beach Towel"], ["🤍", "White"]],
        budgetPrefix: "Under",
        budgets: [399, 799, 1499],
        completeTitle: "Complete the set",
    },
    lungi: {
        color: "#b91c1c",
        gradient: ["#b91c1c", "#2563eb"],
        badgeTop: "Pure cotton",
        badgeBottom: "handloom",
        icon: "🧣",
        item: "piece",
        items: "gamchas & lungis",
        heroEyebrow: "GAMCHAS • LUNGIS • DHOTIS • MUNDUS • THORTHU",
        heroCta: "Shop gamchas & lungis",
        heroStats: ["🧵 Pure cotton", "📦 Dozen packs"],
        searchPlaceholder: "Search gamcha, lungi, dhoti & thorthu",
        searchPrompt: "Find cotton gamchas, lungis and dhotis",
        featuredTitle: "Bestselling gamchas & lungis",
        featuredText: "Pure cotton gamchas, check lungis, dhotis and thorthu towels.",
        readyLine: "gamchas and lungis ready to ship.",
        tagline: "Pure cotton, everyday comfort",
        footerNote: "🧣 Pure cotton gamchas, lungis and dhotis from handloom clusters.",
        guestLine: "Gamchas, lungis & dhotis",
        accountLine: "Your LungiGhar account",
        shopperName: "Customer",
        welcome: "Welcome back 🧣",
        cartTitle: "Your bag",
        cartEmptyIcon: "🧣",
        checkoutHint: "Tell us where to deliver your order.",
        orderDoneLine: "Your gamchas and lungis are being packed.",
        reviewIntro: "Your feedback on cotton and colours helps other shoppers.",
        infoTitle: "Help",
        infoLinks: ["Size guide", "Wholesale", "Contact", "Washing care"],
        detailIntro: "Pure cotton with fast colours, woven by handloom weavers.",
        detailBadge: ["🧵", "Pure cotton", "Fast colours"],
        detailNote: "Dozen packs for shops",
        aboutTitle: "About this product",
        aboutText: "Wash separately the first time in cold water. Dry in shade to keep colours bright.",
        care: [["🧵", "Cotton", "Soft and breathable."], ["🎨", "Fast Colours", "Checks that do not bleed."], ["🤝", "Handloom", "Supports weaver families."]],
        categoryIcons: {"Cotton Gamchas": "🧣", "Everyday Lungis": "👕", "Premium Check Lungis": "🟥", "Dhotis & Mundus": "🤍", "Bath Towels & Thorthu": "🛁"},
        occasionsTitle: "Shop by style",
        occasionsEyebrow: "🧣 Daily wear",
        occasionsSubtitle: "Gamchas, lungis and dhotis for daily wear, pooja and festivals.",
        occasions: [["🧣", "Gamcha"], ["🔴", "Red"], ["🟦", "Check"], ["👕", "Lungi"], ["🤍", "Dhoti"], ["✨", "Kasavu"], ["🛁", "Thorthu"], ["🎨", "Plaid"]],
        budgetPrefix: "Under",
        budgets: [149, 299, 599],
        completeTitle: "Make it a dozen",
    },
    organic: {
        color: "#15803d",
        gradient: ["#15803d", "#84cc16"],
        badgeTop: "Certified",
        badgeBottom: "organic & natural",
        icon: "🌿",
        item: "product",
        items: "organic products",
        heroEyebrow: "GRAINS • COLD-PRESSED OILS • HONEY • HERBAL TEAS",
        heroCta: "Shop organic",
        heroStats: ["🌱 Certified organic", "🫒 Cold-pressed"],
        searchPlaceholder: "Search rice, millets, oils, honey & teas",
        searchPrompt: "Find chemical-free organic food",
        featuredTitle: "Bestselling organic products",
        featuredText: "Organic grains, cold-pressed oils, raw honey and herbal teas.",
        readyLine: "organic products ready to ship.",
        tagline: "Pure food from Indian farms",
        footerNote: "🌿 Certified organic, farmer direct.",
        guestLine: "Organic food & wellness",
        accountLine: "Your organic account",
        shopperName: "Healthy Eater",
        welcome: "Welcome back 🌿",
        cartTitle: "Your organic basket",
        cartEmptyIcon: "🌿",
        checkoutHint: "Tell us where to deliver your organic order.",
        orderDoneLine: "Your organic products are being packed.",
        reviewIntro: "Your feedback on taste and quality helps other shoppers.",
        infoTitle: "Organic Help",
        infoLinks: ["Certification", "Storage tips", "Contact", "Farmers"],
        detailIntro: "Grown without chemicals, processed simply and packed fresh.",
        detailBadge: ["🌱", "Certified organic", "Farmer direct"],
        detailNote: "Store in a cool, dry place",
        aboutTitle: "About this product",
        aboutText: "Store in an airtight container. Natural products may vary slightly in colour and texture.",
        care: [["🌱", "Organic", "No chemicals or pesticides."], ["🫒", "Cold-Pressed", "Nutrients kept intact."], ["👩‍🌾", "Fair", "Fair prices for farmers."]],
        categoryIcons: {"Grains & Pulses": "🌾", "Cold-Pressed Oils": "🫒", "Honey & Jaggery": "🍯", "Herbal Teas": "🍵", "Superfoods & Pantry": "🥣"},
        occasionsTitle: "Shop by kitchen need",
        occasionsEyebrow: "🌿 Eat clean",
        occasionsSubtitle: "Grains, oils, honey and teas for a healthier kitchen.",
        occasions: [["🍚", "Rice"], ["🌾", "Millet"], ["🫘", "Dal"], ["🫒", "Oil"], ["🍯", "Honey"], ["🟫", "Jaggery"], ["🍵", "Tea"], ["🥣", "Seeds"]],
        budgetPrefix: "Under",
        budgets: [199, 499, 999],
        completeTitle: "Complete your organic kitchen",
    },
    murti: {
        color: "#9a3412",
        gradient: ["#9a3412", "#f59e0b"],
        badgeTop: "Handcrafted",
        badgeBottom: "by master artisans",
        icon: "🛕",
        item: "murti",
        items: "murtis & sculptures",
        heroEyebrow: "GANESHA • BUDDHA • RADHA KRISHNA • DURGA • SCULPTURES",
        heroCta: "Shop murtis",
        heroStats: ["🛕 Master artisans", "📦 Safe crate packing"],
        searchPlaceholder: "Search Ganesha, Buddha, Krishna, Durga & statues",
        searchPrompt: "Find a murti for your home or temple",
        featuredTitle: "Most loved murtis",
        featuredText: "Ganesha, Buddha, Radha Krishna and Durga in stone, marble, brass and clay.",
        readyLine: "murtis ready to bless your home.",
        tagline: "Divine art by master artisans",
        footerNote: "🛕 Handcrafted murtis and sculptures, safely crated.",
        guestLine: "Murtis, idols & sculptures",
        accountLine: "Your ShilpKala account",
        shopperName: "Devotee",
        welcome: "Welcome back 🛕",
        cartTitle: "Your murti cart",
        cartEmptyIcon: "🛕",
        checkoutHint: "Tell us where to deliver your murti.",
        orderDoneLine: "Your murti is being crated with care.",
        reviewIntro: "Your feedback on finish and packing helps other devotees.",
        infoTitle: "Murti Help",
        infoLinks: ["Sizes & materials", "Custom orders", "Contact", "Packing"],
        detailIntro: "Carved by hand; small variations make every piece unique.",
        detailBadge: ["🛕", "Handcrafted", "Custom sizes on request"],
        detailNote: "Wooden-crate packing",
        aboutTitle: "About this murti",
        aboutText: "Clean with a soft dry cloth. Keep marble and clay idols away from water and direct sun.",
        care: [["🛕", "Artisans", "Carved by master shilpis."], ["🪨", "Materials", "Stone, marble, brass and clay."], ["📦", "Safe", "Crated and insured delivery."]],
        categoryIcons: {"Ganesha Murtis": "🐘", "Buddha Statues": "🪷", "Radha Krishna Murtis": "🦚", "Durga & Clay Idols": "🔱", "Stone & Garden Sculptures": "🗿"},
        occasionsTitle: "Shop by deity",
        occasionsEyebrow: "🛕 For your mandir",
        occasionsSubtitle: "Murtis for homes, temples, offices and gardens.",
        occasions: [["🐘", "Ganesha"], ["🪷", "Buddha"], ["🦚", "Krishna"], ["🔱", "Durga"], ["🤍", "Marble"], ["🪨", "Stone"], ["✨", "Brass"], ["🌳", "Garden"]],
        budgetPrefix: "Under",
        budgets: [1999, 4999, 9999],
        completeTitle: "Complete your mandir",
    },
    spices: {
        color: "#c2410c",
        gradient: ["#c2410c", "#dc2626"],
        badgeTop: "Pure & fresh",
        badgeBottom: "no colour, no fillers",
        icon: "🌶️",
        item: "pack",
        items: "spices & masalas",
        heroEyebrow: "WHOLE SPICES • POWDERS • MASALAS • BULK",
        heroCta: "Shop spices",
        heroStats: ["⚙️ Stone ground", "📦 100 g to 5 kg"],
        searchPlaceholder: "Search turmeric, chilli, garam masala & whole spices",
        searchPrompt: "Find whole spices, powders and masalas",
        featuredTitle: "Bestselling spices",
        featuredText: "Whole spices, stone-ground powders and masala blends.",
        readyLine: "spices packed fresh.",
        tagline: "Spices straight from the mandi",
        footerNote: "🌶️ Pure spices with no colour or fillers.",
        guestLine: "Spices & masalas",
        accountLine: "Your MasalaMandi account",
        shopperName: "Home Chef",
        welcome: "Welcome back 🌶️",
        cartTitle: "Your masala dabba",
        cartEmptyIcon: "🌶️",
        checkoutHint: "Tell us where to deliver your spices.",
        orderDoneLine: "Your spices are being packed fresh.",
        reviewIntro: "Your feedback on aroma and taste helps other cooks.",
        infoTitle: "Spice Help",
        infoLinks: ["Storage tips", "Bulk orders", "Contact", "Recipes"],
        detailIntro: "Sourced from spice mandis and packed fresh in airtight packets.",
        detailBadge: ["🌶️", "No colour added", "Packed fresh"],
        detailNote: "Bulk prices on 1 kg and above",
        aboutTitle: "About this spice",
        aboutText: "Store in an airtight jar away from sunlight and moisture for the best aroma.",
        care: [["🌶️", "Pure", "No colour or fillers."], ["⚙️", "Stone Ground", "Aroma kept intact."], ["📦", "Bulk", "Packets to 5 kg sacks."]],
        categoryIcons: {"Whole Spices": "🌰", "Spice Powders": "🟠", "Masala Blends": "🍛", "Raw Turmeric, Chillies & Seeds": "🌶️", "Spice Jars & Packets": "🫙"},
        occasionsTitle: "Shop by spice",
        occasionsEyebrow: "🌶️ Flavour for every dish",
        occasionsSubtitle: "Whole spices, powders and masalas for your kitchen or restaurant.",
        occasions: [["🟡", "Turmeric"], ["🌶️", "Chilli"], ["🍛", "Garam Masala"], ["⚫", "Pepper"], ["💚", "Cardamom"], ["🟤", "Cinnamon"], ["🍲", "Sambar"], ["🫙", "Jar"]],
        budgetPrefix: "Under",
        budgets: [149, 299, 599],
        completeTitle: "Complete your masala dabba",
    },
    snacks: {
        color: "#ea580c",
        gradient: ["#ea580c", "#facc15"],
        badgeTop: "Fresh stock",
        badgeBottom: "crunchy every time",
        icon: "🍟",
        item: "pack",
        items: "snacks",
        heroEyebrow: "CHIPS • FRIES • NAMKEEN • ROASTED SNACKS • PARTY PACKS",
        heroCta: "Shop snacks",
        heroStats: ["🥔 Fresh every week", "📦 Wholesale cartons"],
        searchPlaceholder: "Search chips, fries, bhujia, chakli & popcorn",
        searchPrompt: "Find your favourite crunchy snack",
        featuredTitle: "Bestselling snacks",
        featuredText: "Chips, frozen fries, namkeen and roasted snacks.",
        readyLine: "snacks ready to crunch.",
        tagline: "Crunch for every chai time",
        footerNote: "🍟 Fresh chips, fries and namkeen, retail and wholesale.",
        guestLine: "Chips, fries & namkeen",
        accountLine: "Your CrunchKart account",
        shopperName: "Snack Lover",
        welcome: "Welcome back 🍟",
        cartTitle: "Your snack box",
        cartEmptyIcon: "🍟",
        checkoutHint: "Tell us where to deliver your snacks.",
        orderDoneLine: "Your snacks are being packed.",
        reviewIntro: "Your feedback on taste and crunch helps other shoppers.",
        infoTitle: "Snack Help",
        infoLinks: ["Wholesale cartons", "Storage", "Contact", "Bulk orders"],
        detailIntro: "Fresh stock, sealed packs and best-before dates on every pack.",
        detailBadge: ["🥔", "Fresh stock", "Sealed packs"],
        detailNote: "Wholesale cartons available",
        aboutTitle: "About this snack",
        aboutText: "Store in a cool, dry place. Once opened, keep in an airtight container. Frozen fries: keep at -18°C.",
        care: [["🥔", "Fresh", "New batches every week."], ["📦", "Cartons", "Wholesale for shops and events."], ["🌶️", "Flavours", "Salted to spicy."]],
        categoryIcons: {"Potato Chips": "🥔", "Fries & Frozen Snacks": "🍟", "Namkeen & Bhujia": "🥨", "Nuts & Roasted Snacks": "🥜", "Popcorn, Nachos & Party Packs": "🍿"},
        occasionsTitle: "Shop by craving",
        occasionsEyebrow: "🍟 Crunch time",
        occasionsSubtitle: "Chips, fries, namkeen and party packs for every craving.",
        occasions: [["🥔", "Chips"], ["🍟", "Fries"], ["🥨", "Bhujia"], ["🌀", "Chakli"], ["🥜", "Peanuts"], ["🍌", "Banana Chips"], ["🍿", "Popcorn"], ["🧀", "Nachos"]],
        budgetPrefix: "Under",
        budgets: [49, 99, 199],
        completeTitle: "Add a party pack",
    },
    teacoffee: {
        color: "#78350f",
        gradient: ["#78350f", "#d97706"],
        badgeTop: "Fresh packed",
        badgeBottom: "tea & coffee",
        icon: "☕",
        item: "pack",
        items: "teas & coffees",
        heroEyebrow: "LOOSE LEAF TEA • MASALA CHAI • COFFEE • BREWING GEAR",
        heroCta: "Shop tea & coffee",
        heroStats: ["🍃 Garden fresh", "☕ Roasted weekly"],
        searchPlaceholder: "Search Assam tea, masala chai, filter coffee & teapots",
        searchPrompt: "Find your perfect cup",
        featuredTitle: "Bestselling teas & coffees",
        featuredText: "Loose leaf teas, masala chai, roasted coffee and brewing gear.",
        readyLine: "teas and coffees packed fresh.",
        tagline: "Your perfect cup, every day",
        footerNote: "☕ Fresh teas and roasted coffee, packed to order.",
        guestLine: "Tea, chai & coffee",
        accountLine: "Your BrewLeaf account",
        shopperName: "Tea & Coffee Lover",
        welcome: "Welcome back ☕",
        cartTitle: "Your brew basket",
        cartEmptyIcon: "☕",
        checkoutHint: "Tell us where to deliver your tea and coffee.",
        orderDoneLine: "Your tea and coffee are being packed fresh.",
        reviewIntro: "Your feedback on taste and aroma helps other shoppers.",
        infoTitle: "Brew Help",
        infoLinks: ["Brewing guide", "Café packs", "Contact", "Storage"],
        detailIntro: "Packed fresh in airtight pouches; coffee ground to order.",
        detailBadge: ["🍃", "Packed fresh", "Ground to order"],
        detailNote: "Café and office bulk packs",
        aboutTitle: "About this product",
        aboutText: "Store tea and coffee in an airtight jar away from light, heat and strong smells.",
        care: [["🍃", "Fresh", "From the garden to your cup."], ["☕", "Roasted", "Small batch roasting."], ["🏪", "Bulk", "Packs for cafés and offices."]],
        categoryIcons: {"Tea Leaves & Blends": "🍃", "Masala Chai": "🫖", "Coffee Beans & Powder": "☕", "Brewing Equipment": "⚙️", "Teapots & Tea Sets": "🍵"},
        occasionsTitle: "Shop by brew",
        occasionsEyebrow: "☕ Tea or coffee?",
        occasionsSubtitle: "Teas, chai blends, coffees and gear for home and café.",
        occasions: [["🍃", "Green Tea"], ["🫖", "Chai"], ["🌿", "Herbal"], ["☕", "Arabica"], ["🥛", "Filter Coffee"], ["🧊", "Cold Brew"], ["⚙️", "Pour-Over"], ["🍵", "Teapot"]],
        budgetPrefix: "Under",
        budgets: [199, 499, 999],
        completeTitle: "Add a brewing tool",
    },
    gardentools: {
        color: "#4d7c0f",
        gradient: ["#4d7c0f", "#16a34a"],
        badgeTop: "Built to last",
        badgeBottom: "garden tools",
        icon: "🪴",
        item: "tool",
        items: "garden tools",
        heroEyebrow: "HAND TOOLS • PRUNERS • HOSES • SPRINKLERS • KITS",
        heroCta: "Shop garden tools",
        heroStats: ["🛠️ Rust-proof steel", "✋ Easy grip"],
        searchPlaceholder: "Search trowel, pruner, hose, sprinkler & gloves",
        searchPrompt: "Find the right tool for your garden",
        featuredTitle: "Bestselling garden tools",
        featuredText: "Hand tools, pruners, hoses, sprinklers and garden kits.",
        readyLine: "tools ready for your garden.",
        tagline: "Tools for happy gardens",
        footerNote: "🪴 Sturdy garden tools for homes, farms and nurseries.",
        guestLine: "Garden tools & watering",
        accountLine: "Your ToolShed account",
        shopperName: "Gardener",
        welcome: "Welcome back 🪴",
        cartTitle: "Your tool kit",
        cartEmptyIcon: "🪴",
        checkoutHint: "Tell us where to deliver your garden tools.",
        orderDoneLine: "Your garden tools are being packed.",
        reviewIntro: "Your feedback on build and grip helps other gardeners.",
        infoTitle: "Tool Help",
        infoLinks: ["Tool care", "Warranty", "Contact", "Bulk orders"],
        detailIntro: "Rust-proof steel, strong joints and comfortable grips.",
        detailBadge: ["🛠️", "Rust-proof", "Comfort grip"],
        detailNote: "Bulk prices for nurseries and landscapers",
        aboutTitle: "About this tool",
        aboutText: "Clean and dry after use. Oil blades and joints now and then to keep them sharp and smooth.",
        care: [["🛠️", "Sturdy", "Strong steel and joints."], ["✋", "Comfort", "Non-slip grips."], ["💧", "Water Smart", "Hoses and sprinklers that save water."]],
        categoryIcons: {"Hand Tools": "🪴", "Pruners & Shears": "✂️", "Watering & Irrigation": "💧", "Gloves, Kits & Accessories": "🧤", "Large Tools & Garden Care": "🛒"},
        occasionsTitle: "Shop by job",
        occasionsEyebrow: "🪴 Garden ready",
        occasionsSubtitle: "Dig, prune, water and care for your plants.",
        occasions: [["🪴", "Trowel"], ["🔱", "Fork"], ["✂️", "Pruner"], ["💧", "Hose"], ["💦", "Sprinkler"], ["🧤", "Gloves"], ["🛒", "Wheelbarrow"], ["🌱", "Kit"]],
        budgetPrefix: "Under",
        budgets: [349, 799, 1499],
        completeTitle: "Add gloves and a kit",
    },
    pickles: {
        color: "#b91c1c",
        gradient: ["#b91c1c", "#f59e0b"],
        badgeTop: "Homemade",
        badgeBottom: "in small batches",
        icon: "🥭",
        item: "jar",
        items: "pickles & podis",
        heroEyebrow: "AVAKAYA • GONGURA • THOKKU • PODIS • MASALAS",
        heroCta: "Shop pickles",
        heroStats: ["🏠 Homemade", "📦 Leak-proof packing"],
        searchPlaceholder: "Search avakaya, gongura, idli podi & masalas",
        searchPrompt: "Find a pickle just like home",
        featuredTitle: "Bestselling pickles & podis",
        featuredText: "Andhra and Tamil pickles, podis and homemade masalas.",
        readyLine: "pickles & podis ready for you.",
        tagline: "Taste of home, in every jar",
        footerNote: "🥭 Taste of home, in every jar.",
        guestLine: "Taste of home, in every jar",
        accountLine: "Your account",
        shopperName: "Customer",
        welcome: "Welcome back 🥭",
        cartTitle: "Your pickle basket",
        cartEmptyIcon: "🥭",
        checkoutHint: "Tell us where to deliver your order.",
        orderDoneLine: "Your order is being prepared with care.",
        reviewIntro: "Your feedback helps other shoppers choose.",
        infoTitle: "Help",
        infoLinks: ["Ordering", "Delivery", "Contact", "Returns"],
        detailIntro: "Made at home in small batches with cold-pressed gingelly oil.",
        detailBadge: ["🏠", "Homemade", "Leak-proof jars"],
        detailNote: "Ships across India and abroad",
        aboutTitle: "About this product",
        aboutText: "Use a dry spoon. Keep the jar closed and away from moisture. Refrigerate after opening in summer.",
        care: [["🛢️", "Gingelly Oil", "Cured in cold-pressed oil."], ["🚫", "No Preservatives", "Only salt, oil and spices."], ["✈️", "Travel Packs", "Leak-proof jars for NRIs."]],
        categoryIcons: {"Andhra & Mango Pickles": "🥭", "Vegetable & Mixed Pickles": "🥕", "Idli & Rice Podis": "🍚", "Chutneys & Masalas": "🌶️", "Combo & Gift Packs": "🎁"},
        occasionsTitle: "Shop by taste",
        occasionsEyebrow: "🥭 Ammas' recipes",
        occasionsSubtitle: "Tangy, spicy and homemade, the way it should be.",
        occasions: [["🥭", "Mango"], ["🌿", "Gongura"], ["🍋", "Lemon"], ["🧄", "Garlic"], ["🌶️", "Chilli"], ["🍚", "Podi"], ["🍛", "Masala"], ["🎁", "Combo"]],
        budgetPrefix: "Under",
        budgets: [199, 299, 599],
        completeTitle: "Add a podi to your order",
    },
    bakery: {
        color: "#db2777",
        gradient: ["#db2777", "#f472b6"],
        badgeTop: "Baked fresh",
        badgeBottom: "made to order",
        icon: "🎂",
        item: "cake",
        items: "cakes & bakes",
        heroEyebrow: "BIRTHDAY • THEME CAKES • CUPCAKES • COOKIES",
        heroCta: "Order a cake",
        heroStats: ["🥚 Eggless available", "🎨 Custom themes"],
        searchPlaceholder: "Search birthday, theme, chocolate & eggless cakes",
        searchPrompt: "Find the perfect cake for your celebration",
        featuredTitle: "Most loved cakes",
        featuredText: "Birthday, theme and celebration cakes with your message.",
        readyLine: "cakes & bakes ready for you.",
        tagline: "Baked with love, made to order",
        footerNote: "🎂 Baked with love, made to order.",
        guestLine: "Baked with love, made to order",
        accountLine: "Your account",
        shopperName: "Customer",
        welcome: "Welcome back 🎂",
        cartTitle: "Your cake order",
        cartEmptyIcon: "🎂",
        checkoutHint: "Tell us where to deliver your order.",
        orderDoneLine: "Your order is being prepared with care.",
        reviewIntro: "Your feedback helps other shoppers choose.",
        infoTitle: "Help",
        infoLinks: ["Ordering", "Delivery", "Contact", "Returns"],
        detailIntro: "Baked fresh to order; eggless on request. Order 24 hours ahead (theme cakes 3 days).",
        detailBadge: ["🎂", "Made to order", "Eggless available"],
        detailNote: "Choose your delivery date and time at checkout",
        aboutTitle: "About this product",
        aboutText: "Keep cream cakes refrigerated and eat within 24 hours. Fondant cakes: keep in a cool place, not the fridge.",
        care: [["🥚", "Eggless", "Any cake can be eggless."], ["🎨", "Custom", "Your theme, name and photo."], ["⏰", "Fresh", "Baked on the day of delivery."]],
        categoryIcons: {"Birthday Cakes": "🎂", "Theme & Custom Cakes": "🦁", "Celebration & Wedding Cakes": "💍", "Cupcakes & Brownies": "🧁", "Cookies & Dessert Boxes": "🍪"},
        occasionsTitle: "Shop by occasion",
        occasionsEyebrow: "🎉 Every celebration",
        occasionsSubtitle: "Birthdays, anniversaries, weddings and little treats.",
        occasions: [["🎂", "Birthday"], ["🍫", "Chocolate"], ["🦁", "Theme"], ["💍", "Wedding"], ["❤️", "Heart"], ["🧁", "Cupcake"], ["🍫", "Brownie"], ["🍪", "Cookie"]],
        budgetPrefix: "Under",
        budgets: [499, 999, 1999],
        completeTitle: "Add cupcakes or cookies",
    },
    jewellery: {
        color: "#a16207",
        gradient: ["#a16207", "#f59e0b"],
        badgeTop: "Looks like",
        badgeBottom: "real gold",
        icon: "💛",
        item: "piece",
        items: "jewellery",
        heroEyebrow: "TEMPLE • KEMP • ANTIQUE • JHUMKAS • BANGLES",
        heroCta: "Shop jewellery",
        heroStats: ["✨ Gold-look finish", "💵 COD available"],
        searchPlaceholder: "Search temple sets, jhumkas, necklaces & bangles",
        searchPrompt: "Find jewellery for every saree",
        featuredTitle: "Bestselling jewellery",
        featuredText: "Temple, kemp, antique and daily wear jewellery.",
        readyLine: "jewellery ready for you.",
        tagline: "Real gold look, everyday prices",
        footerNote: "💛 Real gold look, everyday prices.",
        guestLine: "Real gold look, everyday prices",
        accountLine: "Your account",
        shopperName: "Customer",
        welcome: "Welcome back 💛",
        cartTitle: "Your jewellery box",
        cartEmptyIcon: "💛",
        checkoutHint: "Tell us where to deliver your order.",
        orderDoneLine: "Your order is being prepared with care.",
        reviewIntro: "Your feedback helps other shoppers choose.",
        infoTitle: "Help",
        infoLinks: ["Ordering", "Delivery", "Contact", "Returns"],
        detailIntro: "Gold-look plating on brass and copper alloy, finished by hand.",
        detailBadge: ["✨", "Gold-look finish", "7-day exchange"],
        detailNote: "Keep away from water and perfume",
        aboutTitle: "About this product",
        aboutText: "Store in the box or a zip pouch. Avoid water, sweat and perfume to keep the shine.",
        care: [["✨", "Gold Look", "Micro gold plating."], ["💍", "Bridal", "Complete wedding sets."], ["🔁", "Exchange", "7-day exchange on unused pieces."]],
        categoryIcons: {"Temple & Bridal Sets": "🛕", "Necklaces & Chokers": "📿", "Jhumkas & Earrings": "🔔", "Bangles & Kadas": "⭕", "Daily Wear & Pendants": "✨"},
        occasionsTitle: "Shop by style",
        occasionsEyebrow: "💛 For every occasion",
        occasionsSubtitle: "Bridal sets, jhumkas and everyday pieces.",
        occasions: [["🛕", "Temple"], ["💎", "Kemp"], ["🔔", "Jhumka"], ["📿", "Necklace"], ["⭕", "Bangle"], ["🤍", "Pearl"], ["🩶", "Oxidised"], ["👰", "Bridal"]],
        budgetPrefix: "Under",
        budgets: [499, 999, 1999],
        completeTitle: "Complete the look with jhumkas",
    },
    sarees: {
        color: "#7e22ce",
        gradient: ["#7e22ce", "#db2777"],
        badgeTop: "Weaver",
        badgeBottom: "direct",
        icon: "🥻",
        item: "saree",
        items: "sarees",
        heroEyebrow: "KANCHIPURAM • SOFT SILK • HANDLOOM • KERALA KASAVU",
        heroCta: "Shop sarees",
        heroStats: ["🧵 Weaver direct", "✂️ Blouse stitching"],
        searchPlaceholder: "Search Kanchipuram, soft silk, cotton & kasavu",
        searchPrompt: "Find your next saree",
        featuredTitle: "New saree drops",
        featuredText: "Kanchipuram silk, soft silk, handloom cotton and Kerala sarees.",
        readyLine: "sarees ready for you.",
        tagline: "Sarees straight from the loom",
        footerNote: "🥻 Sarees straight from the loom.",
        guestLine: "Sarees straight from the loom",
        accountLine: "Your account",
        shopperName: "Customer",
        welcome: "Welcome back 🥻",
        cartTitle: "Your saree bag",
        cartEmptyIcon: "🥻",
        checkoutHint: "Tell us where to deliver your order.",
        orderDoneLine: "Your order is being prepared with care.",
        reviewIntro: "Your feedback helps other shoppers choose.",
        infoTitle: "Help",
        infoLinks: ["Ordering", "Delivery", "Contact", "Returns"],
        detailIntro: "Weaver-direct saree with blouse piece; slight colour variation is natural in handloom.",
        detailBadge: ["🧵", "Weaver direct", "Blouse stitching available"],
        detailNote: "Dry clean silk sarees",
        aboutTitle: "About this product",
        aboutText: "Dry clean silk. Store wrapped in a cotton cloth and refold every few months.",
        care: [["🧵", "Weaver Direct", "Bought from weaver families."], ["✅", "Silk Mark", "On pure silk sarees."], ["📹", "Video", "Ask for a video on WhatsApp."]],
        categoryIcons: {"Kanchipuram Silk Sarees": "👑", "Soft Silk Sarees": "🌸", "Cotton & Handloom Sarees": "🌿", "Kerala Kasavu & Set Sarees": "🤍", "Party & Festive Sarees": "✨"},
        occasionsTitle: "Shop by weave",
        occasionsEyebrow: "🥻 From the looms",
        occasionsSubtitle: "Silk, cotton and handloom sarees for every occasion.",
        occasions: [["👑", "Kanchipuram"], ["🌸", "Soft Silk"], ["🌿", "Cotton"], ["🤍", "Kasavu"], ["🧵", "Handloom"], ["🔴", "Red"], ["💚", "Green"], ["✨", "Festive"]],
        budgetPrefix: "Under",
        budgets: [1999, 4999, 9999],
        completeTitle: "Add a matching blouse",
    },
    sweets: {
        color: "#ea580c",
        gradient: ["#ea580c", "#facc15"],
        badgeTop: "Pure ghee",
        badgeBottom: "homemade",
        icon: "🪔",
        item: "box",
        items: "sweets",
        heroEyebrow: "LADDUS • MYSORE PAK • HALWA • FESTIVAL BOXES",
        heroCta: "Shop sweets",
        heroStats: ["🧈 Pure ghee", "🎁 Gift boxes"],
        searchPlaceholder: "Search laddu, Mysore pak, halwa & gift boxes",
        searchPrompt: "Find sweets for every celebration",
        featuredTitle: "Bestselling sweets",
        featuredText: "Laddus, ghee sweets, halwa and festival boxes.",
        readyLine: "sweets ready for you.",
        tagline: "Homemade sweets in pure ghee",
        footerNote: "🪔 Homemade sweets in pure ghee.",
        guestLine: "Homemade sweets in pure ghee",
        accountLine: "Your account",
        shopperName: "Customer",
        welcome: "Welcome back 🪔",
        cartTitle: "Your sweet box",
        cartEmptyIcon: "🪔",
        checkoutHint: "Tell us where to deliver your order.",
        orderDoneLine: "Your order is being prepared with care.",
        reviewIntro: "Your feedback helps other shoppers choose.",
        infoTitle: "Help",
        infoLinks: ["Ordering", "Delivery", "Contact", "Returns"],
        detailIntro: "Made fresh in pure ghee and packed airtight.",
        detailBadge: ["🧈", "Pure ghee", "Fresh every day"],
        detailNote: "Best within 10 days",
        aboutTitle: "About this product",
        aboutText: "Keep in an airtight box in a cool place. Milk sweets: refrigerate and eat within 3 days.",
        care: [["🧈", "Pure Ghee", "Only pure cow ghee."], ["🏠", "Homemade", "Fresh small batches."], ["🎁", "Gifting", "Boxes for every festival."]],
        categoryIcons: {"Laddus": "🟠", "Ghee Sweets & Barfi": "🧈", "Halwa & Milk Sweets": "🍮", "Festival Platters": "🪔", "Gift Boxes & Combos": "🎁"},
        occasionsTitle: "Shop by sweet",
        occasionsEyebrow: "🪔 Festive favourites",
        occasionsSubtitle: "Laddus, barfis, halwa and festival boxes.",
        occasions: [["🟠", "Laddu"], ["🧈", "Mysore Pak"], ["🍮", "Halwa"], ["🥥", "Coconut"], ["🥜", "Kaju"], ["🍯", "Jaggery"], ["🪔", "Diwali"], ["🎁", "Gift"]],
        budgetPrefix: "Under",
        budgets: [299, 599, 999],
        completeTitle: "Make it a gift box",
    },
    herbal: {
        color: "#15803d",
        gradient: ["#15803d", "#84cc16"],
        badgeTop: "100% herbal",
        badgeBottom: "no chemicals",
        icon: "🌿",
        item: "bottle",
        items: "herbal products",
        heroEyebrow: "HAIR OILS • HERBAL POWDERS • FACE PACKS • SKIN CARE",
        heroCta: "Shop herbal care",
        heroStats: ["🌿 All natural", "👵 Traditional recipes"],
        searchPlaceholder: "Search hair oil, shikakai, face pack & kumkumadi",
        searchPrompt: "Find natural care for hair and skin",
        featuredTitle: "Bestselling herbal care",
        featuredText: "Hair oils, herbal powders, face packs and skin care.",
        readyLine: "herbal products ready for you.",
        tagline: "Natural care from traditional recipes",
        footerNote: "🌿 Natural care from traditional recipes.",
        guestLine: "Natural care from traditional recipes",
        accountLine: "Your account",
        shopperName: "Customer",
        welcome: "Welcome back 🌿",
        cartTitle: "Your herbal basket",
        cartEmptyIcon: "🌿",
        checkoutHint: "Tell us where to deliver your order.",
        orderDoneLine: "Your order is being prepared with care.",
        reviewIntro: "Your feedback helps other shoppers choose.",
        infoTitle: "Help",
        infoLinks: ["Ordering", "Delivery", "Contact", "Returns"],
        detailIntro: "Made in small batches from herbs and cold-pressed oils.",
        detailBadge: ["🌿", "All natural", "Patch test advised"],
        detailNote: "Do a patch test before first use",
        aboutTitle: "About this product",
        aboutText: "Store in a cool, dark place. Do a small patch test before first use.",
        care: [["🌿", "Natural", "Herbs and cold-pressed oils."], ["🧪", "Fresh", "Small batches every week."], ["💬", "Advice", "Free hair advice on WhatsApp."]],
        categoryIcons: {"Herbal Hair Oils": "💧", "Hair Masks & Serums": "🧴", "Herbal Powders & Face Packs": "🍃", "Skin Care & Bath": "✨", "Wellness & Massage Oils": "🪷"},
        occasionsTitle: "Shop by concern",
        occasionsEyebrow: "🌿 Natural care",
        occasionsSubtitle: "Hair fall, dandruff, dull skin and more.",
        occasions: [["💧", "Hair Oil"], ["🌺", "Hibiscus"], ["🌿", "Rosemary"], ["🍃", "Shikakai"], ["🟡", "Turmeric"], ["🌹", "Rose"], ["🧴", "Serum"], ["💆", "Massage"]],
        budgetPrefix: "Under",
        budgets: [249, 499, 999],
        completeTitle: "Complete your routine",
    },
    healthmix: {
        color: "#a16207",
        gradient: ["#a16207", "#65a30d"],
        badgeTop: "Sprouted",
        badgeBottom: "no sugar added",
        icon: "🌾",
        item: "pack",
        items: "health mixes",
        heroEyebrow: "SATHU MAAVU • RAGI MALT • BABY FOOD • MILLETS",
        heroCta: "Shop health mix",
        heroStats: ["🌱 Sprouted grains", "🚫 No sugar"],
        searchPlaceholder: "Search sathu maavu, ragi, baby food & millets",
        searchPrompt: "Find healthy mixes for the family",
        featuredTitle: "Bestselling health mixes",
        featuredText: "Sathu maavu, ragi malt, baby food and millets.",
        readyLine: "health mixes ready for you.",
        tagline: "Healthy mixes, made at home",
        footerNote: "🌾 Healthy mixes, made at home.",
        guestLine: "Healthy mixes, made at home",
        accountLine: "Your account",
        shopperName: "Customer",
        welcome: "Welcome back 🌾",
        cartTitle: "Your health basket",
        cartEmptyIcon: "🌾",
        checkoutHint: "Tell us where to deliver your order.",
        orderDoneLine: "Your order is being prepared with care.",
        reviewIntro: "Your feedback helps other shoppers choose.",
        infoTitle: "Help",
        infoLinks: ["Ordering", "Delivery", "Contact", "Returns"],
        detailIntro: "Sprouted, roasted and stone-ground in small batches.",
        detailBadge: ["🌱", "Sprouted grains", "No sugar added"],
        detailNote: "Consult your paediatrician for babies",
        aboutTitle: "About this product",
        aboutText: "Store in an airtight container. Use within 60 days of opening.",
        care: [["🌱", "Sprouted", "Easy to digest."], ["🚫", "No Sugar", "No sugar or preservatives."], ["👶", "Baby Safe", "Gentle first foods."]],
        categoryIcons: {"Health Mix & Sathu Maavu": "🥣", "Ragi & Millet Porridge": "🌾", "Baby Food (6 months+)": "👶", "Millet Breakfast Bowls": "🥞", "Whole Millets & Grains": "🌱"},
        occasionsTitle: "Shop by need",
        occasionsEyebrow: "🌾 Healthy family",
        occasionsSubtitle: "For kids, adults and little babies.",
        occasions: [["🥣", "Health Mix"], ["🌾", "Ragi"], ["👶", "Baby"], ["🍌", "Banana"], ["🥞", "Dosa Mix"], ["🥣", "Granola"], ["🌱", "Millet"], ["🍫", "Choco"]],
        budgetPrefix: "Under",
        budgets: [199, 349, 699],
        completeTitle: "Add a millet pack",
    },
    dailywear: {
        color: "#be185d",
        gradient: ["#be185d", "#8b5cf6"],
        badgeTop: "Tirupur cotton",
        badgeBottom: "S to 5XL",
        icon: "👚",
        item: "piece",
        items: "daily wear",
        heroEyebrow: "KURTIS • NIGHTWEAR • LOUNGE SETS • LEGGINGS",
        heroCta: "Shop daily wear",
        heroStats: ["🧵 Pure cotton", "📏 S to 5XL"],
        searchPlaceholder: "Search kurti, nighty, lounge set & leggings",
        searchPrompt: "Find comfy everyday wear",
        featuredTitle: "Bestselling daily wear",
        featuredText: "Cotton kurtis, nightwear, lounge sets and leggings.",
        readyLine: "daily wear ready for you.",
        tagline: "Comfy cotton for every day",
        footerNote: "👚 Comfy cotton for every day.",
        guestLine: "Comfy cotton for every day",
        accountLine: "Your account",
        shopperName: "Customer",
        welcome: "Welcome back 👚",
        cartTitle: "Your bag",
        cartEmptyIcon: "👚",
        checkoutHint: "Tell us where to deliver your order.",
        orderDoneLine: "Your order is being prepared with care.",
        reviewIntro: "Your feedback helps other shoppers choose.",
        infoTitle: "Help",
        infoLinks: ["Ordering", "Delivery", "Contact", "Returns"],
        detailIntro: "Tirupur cotton with fast colours; true to size.",
        detailBadge: ["🧵", "Pure cotton", "Easy size exchange"],
        detailNote: "Reseller packs available",
        aboutTitle: "About this product",
        aboutText: "Wash in cold water with similar colours. Dry in shade.",
        care: [["🧵", "Cotton", "Soft and breathable."], ["📏", "Sizes", "S to 5XL."], ["🛍️", "Resellers", "Wholesale packs available."]],
        categoryIcons: {"Cotton Kurtis": "👗", "Printed Daily Kurtis": "🌸", "Nightwear & Pyjama Sets": "🌙", "Lounge & Satin Sets": "✨", "Leggings & Activewear": "🏃‍♀️"},
        occasionsTitle: "Shop by style",
        occasionsEyebrow: "👚 Everyday comfort",
        occasionsSubtitle: "Kurtis, nightwear and leggings in every size.",
        occasions: [["👗", "Kurti"], ["🌸", "Printed"], ["🌙", "Night"], ["✨", "Satin"], ["🛋️", "Lounge"], ["🏃‍♀️", "Leggings"], ["🧘", "Yoga"], ["🤍", "Cotton"]],
        budgetPrefix: "Under",
        budgets: [399, 699, 999],
        completeTitle: "Add leggings to match",
    },
    tiffin: {
        color: "#c2410c",
        gradient: ["#c2410c", "#16a34a"],
        badgeTop: "Home cooked",
        badgeBottom: "fresh every day",
        icon: "🍛",
        item: "plate",
        items: "home food",
        heroEyebrow: "TIFFIN • MEALS • BIRYANI • CATERING",
        heroCta: "Order food",
        heroStats: ["🏠 Home cooked", "⏰ On-time delivery"],
        searchPlaceholder: "Search dosa, idli, meals, biryani & catering",
        searchPrompt: "Find today's home-cooked food",
        featuredTitle: "Today's favourites",
        featuredText: "Tiffin, meals, biryani and party catering.",
        readyLine: "home food ready for you.",
        tagline: "Home-cooked food, delivered hot",
        footerNote: "🍛 Home-cooked food, delivered hot.",
        guestLine: "Home-cooked food, delivered hot",
        accountLine: "Your account",
        shopperName: "Customer",
        welcome: "Welcome back 🍛",
        cartTitle: "Your food order",
        cartEmptyIcon: "🍛",
        checkoutHint: "Tell us where to deliver your order.",
        orderDoneLine: "Your order is being prepared with care.",
        reviewIntro: "Your feedback helps other shoppers choose.",
        infoTitle: "Help",
        infoLinks: ["Ordering", "Delivery", "Contact", "Returns"],
        detailIntro: "Cooked fresh at home on the day of delivery.",
        detailBadge: ["🏠", "Home cooked", "Order by 10 am for lunch"],
        detailNote: "Delivery within 5 km",
        aboutTitle: "About this product",
        aboutText: "Eat within 2 hours of delivery. Reheat gently; do not refreeze.",
        care: [["🏠", "Homemade", "Cooked fresh at home."], ["⏰", "On Time", "Hot food in your slot."], ["📅", "Plans", "Weekly and monthly plans."]],
        categoryIcons: {"Breakfast Tiffin": "🥞", "Vada & Evening Snacks": "🍩", "Meals & Thalis": "🍛", "Biryani & Rice": "🍗", "Party & Catering": "🎉"},
        occasionsTitle: "Shop by meal",
        occasionsEyebrow: "🍛 Fresh from our kitchen",
        occasionsSubtitle: "Breakfast, lunch, dinner and parties.",
        occasions: [["🥞", "Dosa"], ["⚪", "Idli"], ["🍩", "Vada"], ["🍛", "Thali"], ["🍗", "Biryani"], ["🌿", "Sadya"], ["🥥", "Appam"], ["🎉", "Party"]],
        budgetPrefix: "Under",
        budgets: [99, 199, 999],
        completeTitle: "Add a vada or sweet",
    },
    bangles: {
        color: "#e11d48",
        gradient: ["#e11d48", "#a855f7"],
        badgeTop: "Colour",
        badgeBottom: "matched to you",
        icon: "💍",
        item: "set",
        items: "bangles & accessories",
        heroEyebrow: "SILK THREAD • BRIDAL BANGLES • HAIR ACCESSORIES • JASMINE",
        heroCta: "Shop bangles",
        heroStats: ["🎨 Colour matched", "👰 Bridal sets"],
        searchPlaceholder: "Search silk thread, bridal bangles, clips & jasmine",
        searchPrompt: "Find bangles that match your outfit",
        featuredTitle: "Bestselling bangles",
        featuredText: "Silk thread and bridal bangles, hair accessories and jasmine.",
        readyLine: "bangles & accessories ready for you.",
        tagline: "Bangles made to match your outfit",
        footerNote: "💍 Bangles made to match your outfit.",
        guestLine: "Bangles made to match your outfit",
        accountLine: "Your account",
        shopperName: "Customer",
        welcome: "Welcome back 💍",
        cartTitle: "Your jewellery bag",
        cartEmptyIcon: "💍",
        checkoutHint: "Tell us where to deliver your order.",
        orderDoneLine: "Your order is being prepared with care.",
        reviewIntro: "Your feedback helps other shoppers choose.",
        infoTitle: "Help",
        infoLinks: ["Ordering", "Delivery", "Contact", "Returns"],
        detailIntro: "Handmade to your size and colours; made-to-order sets take 3-5 days.",
        detailBadge: ["🎨", "Colour matched", "Made to order"],
        detailNote: "Measure your bangle size before ordering",
        aboutTitle: "About this product",
        aboutText: "Keep away from water. Store in a box to keep the threads fresh.",
        care: [["🎨", "Matched", "Send your outfit photo."], ["👰", "Bridal", "Sets for every ritual."], ["🌼", "Fresh Jasmine", "Same-day gajra."]],
        categoryIcons: {"Glass & Silk Thread Bangles": "🟣", "Bridal Bangle Sets": "👰", "Hair Clips & Accessories": "🎀", "Jasmine & Bridal Hair": "🌼", "Bridal Kits & Bulk Packs": "📦"},
        occasionsTitle: "Shop by look",
        occasionsEyebrow: "💍 Match your outfit",
        occasionsSubtitle: "Bangles and hair accessories for every outfit.",
        occasions: [["🟣", "Silk Thread"], ["🌈", "Glass"], ["👰", "Bridal"], ["🔴", "Red"], ["💚", "Green"], ["🎀", "Clips"], ["🌼", "Jasmine"], ["📦", "Bulk"]],
        budgetPrefix: "Under",
        budgets: [199, 499, 999],
        completeTitle: "Add hair accessories",
    },
    pooja: {
        color: "#c2410c",
        gradient: ["#c2410c", "#f59e0b"],
        badgeTop: "Pure brass",
        badgeBottom: "temple quality",
        icon: "🪔",
        item: "item",
        items: "pooja items",
        heroEyebrow: "BRASS DIYAS • POOJA THALIS • RANGOLI • INCENSE",
        heroCta: "Shop pooja items",
        heroStats: ["🪔 Pure brass", "🎁 Festival combos"],
        searchPlaceholder: "Search diyas, thalis, rangoli, agarbatti & idols",
        searchPrompt: "Find everything for your daily pooja",
        featuredTitle: "Bestselling pooja items",
        featuredText: "Brass diyas, thalis, rangoli and incense for every festival.",
        readyLine: "pooja items ready for your mandir.",
        tagline: "Everything for your home mandir",
        footerNote: "🪔 Everything for your home mandir.",
        guestLine: "Everything for your home mandir",
        accountLine: "Your account",
        shopperName: "Devotee",
        welcome: "Welcome back 🪔",
        cartTitle: "Your pooja basket",
        cartEmptyIcon: "🪔",
        checkoutHint: "Tell us where to deliver your order.",
        orderDoneLine: "Your order is being packed with care.",
        reviewIntro: "Your feedback helps other shoppers choose.",
        infoTitle: "Help",
        infoLinks: ["Ordering", "Delivery", "Contact", "Returns"],
        detailIntro: "Pure brass and handmade items, packed safely for delivery.",
        detailBadge: ["🪔", "Pure brass", "Temple quality"],
        detailNote: "Clean brass with tamarind or lemon to keep it shining",
        aboutTitle: "About this product",
        aboutText: "Wipe with a soft dry cloth. Polish brass once a month.",
        care: [["🪔", "Pure Brass", "Heavy, long-lasting brass."], ["🎁", "Combos", "Festival-ready kits."], ["📦", "Safe Packing", "Bubble-wrapped and boxed."]],
        categoryIcons: {"Brass Diyas & Lamps": "🪔", "Pooja Thalis & Sets": "🍽️", "Rangoli & Kolam Decor": "🌸", "Incense & Dhoop": "🕯️", "Idols, Bells & Mandir Decor": "🔔"},
        occasionsTitle: "Shop by festival",
        occasionsEyebrow: "🪔 For every pooja",
        occasionsSubtitle: "Diyas, thalis and decor for daily pooja and festivals.",
        occasions: [["🪔", "Diya"], ["🍽️", "Thali"], ["🌸", "Rangoli"], ["🕯️", "Agarbatti"], ["🔔", "Bell"], ["🐘", "Ganesha"], ["🥥", "Kalash"], ["✨", "Lamp"]],
        budgetPrefix: "Pooja items under",
        budgets: [299, 599, 999],
        completeTitle: "Complete your pooja set",
    },
    electronics: {
        color: "#2563eb",
        gradient: ["#2563eb", "#06b6d4"],
        badgeTop: "6-month",
        badgeBottom: "warranty",
        icon: "🎧",
        item: "gadget",
        items: "gadgets",
        heroEyebrow: "EARBUDS • SMART WATCHES • CHARGERS • SPEAKERS",
        heroCta: "Shop gadgets",
        heroStats: ["🛡️ 6-month warranty", "🔁 7-day replacement"],
        searchPlaceholder: "Search earbuds, smart watches, chargers & speakers",
        searchPrompt: "Find gadgets at shop prices",
        featuredTitle: "Trending gadgets",
        featuredText: "Earbuds, smart watches and fast chargers people love.",
        readyLine: "gadgets ready to ship.",
        tagline: "Gadgets with warranty, delivered fast",
        footerNote: "🎧 Gadgets with warranty, delivered fast.",
        guestLine: "Gadgets with warranty, delivered fast",
        accountLine: "Your account",
        shopperName: "Customer",
        welcome: "Welcome back ⚡",
        cartTitle: "Your cart",
        cartEmptyIcon: "🎧",
        checkoutHint: "Tell us where to deliver your order.",
        orderDoneLine: "Your order is being packed with care.",
        reviewIntro: "Your feedback helps other shoppers choose.",
        infoTitle: "Help",
        infoLinks: ["Ordering", "Delivery", "Contact", "Returns"],
        detailIntro: "Every gadget is checked before dispatch and covered by warranty.",
        detailBadge: ["🛡️", "6-month warranty", "7-day replacement"],
        detailNote: "Keep the box and bill for warranty claims",
        aboutTitle: "About this product",
        aboutText: "Charge with the cable in the box. Keep away from water unless rated waterproof.",
        care: [["🛡️", "Warranty", "6 months on every gadget."], ["🔁", "Replacement", "7-day replacement."], ["🚚", "Fast Delivery", "Dispatched in 24 hours."]],
        categoryIcons: {"Earbuds & Earphones": "🎧", "Headphones": "🎵", "Chargers, Cables & Power Banks": "🔌", "Smart Watches": "⌚", "Speakers & Smart Home": "🔊"},
        occasionsTitle: "Shop by gadget",
        occasionsEyebrow: "⚡ Tech for everyday",
        occasionsSubtitle: "Audio, wearables, charging and smart home.",
        occasions: [["🎧", "Earbuds"], ["🎵", "Headphones"], ["⌚", "Smart Watch"], ["🔋", "Power Bank"], ["🔌", "Charger"], ["🔊", "Speaker"], ["💡", "Smart Bulb"], ["🧒", "Kids"]],
        budgetPrefix: "Gadgets under",
        budgets: [999, 1999, 2999],
        completeTitle: "Add a charger or cable",
    },
    comics: {
        color: "#dc2626",
        gradient: ["#dc2626", "#facc15"],
        badgeTop: "Bagged",
        badgeBottom: "& boarded",
        icon: "💥",
        item: "comic",
        items: "comics",
        heroEyebrow: "MARVEL • DC • RAJ COMICS • MANGA",
        heroCta: "Shop comics",
        heroStats: ["📚 New & pre-loved", "🛡️ Bagged & boarded"],
        searchPlaceholder: "Search Marvel, DC, Raj Comics, manga & figures",
        searchPrompt: "Find your next favourite comic",
        featuredTitle: "Fan favourites",
        featuredText: "Superhero issues, Indian comics and manga volumes.",
        readyLine: "comics ready to read.",
        tagline: "Comics for readers and collectors",
        footerNote: "💥 Comics for readers and collectors.",
        guestLine: "Comics for readers and collectors",
        accountLine: "Your account",
        shopperName: "Reader",
        welcome: "Welcome back 💥",
        cartTitle: "Your comic pile",
        cartEmptyIcon: "📚",
        checkoutHint: "Tell us where to deliver your order.",
        orderDoneLine: "Your order is being packed with care.",
        reviewIntro: "Your feedback helps other shoppers choose.",
        infoTitle: "Help",
        infoLinks: ["Ordering", "Delivery", "Contact", "Returns"],
        detailIntro: "Every comic is checked, sleeved and packed flat.",
        detailBadge: ["🛡️", "Bagged & boarded", "Packed flat"],
        detailNote: "Pre-loved issues may show light reading wear",
        aboutTitle: "About this product",
        aboutText: "Store flat in a sleeve, away from sunlight and damp.",
        care: [["📚", "Huge Collection", "Superhero, Indian and manga."], ["🛡️", "Safe Shipping", "Sleeved and packed flat."], ["♻️", "Pre-Loved", "Old issues at half price."]],
        categoryIcons: {"Marvel Comics": "🦸", "DC Comics": "🦇", "Raj & Indian Comics": "🐍", "Manga & Graphic Novels": "🗾", "Collectibles & Merch": "🧸"},
        occasionsTitle: "Shop by universe",
        occasionsEyebrow: "💥 Pick your heroes",
        occasionsSubtitle: "Superheroes, Indian comics, manga and collectibles.",
        occasions: [["🦾", "Iron Man"], ["🦇", "Batman"], ["🐍", "Nagraj"], ["🗾", "Manga"], ["📖", "Graphic Novel"], ["🧸", "Figure"], ["📦", "Bundle"], ["♻️", "Pre-Loved"]],
        budgetPrefix: "Comics under",
        budgets: [499, 999, 1999],
        completeTitle: "Add a figure for your shelf",
    },
    coconutoil: {
        color: "#65a30d",
        gradient: ["#65a30d", "#a16207"],
        badgeTop: "Wood",
        badgeBottom: "pressed",
        icon: "🥥",
        item: "bottle",
        items: "coconut products",
        heroEyebrow: "WOOD-PRESSED • VIRGIN • HAIR OILS • SOAPS",
        heroCta: "Shop coconut oil",
        heroStats: ["🪵 Wood pressed", "🚫 Nothing added"],
        searchPlaceholder: "Search coconut oil, virgin oil, hair oil & soaps",
        searchPrompt: "Find pure homemade coconut oil",
        featuredTitle: "Bestselling oils",
        featuredText: "Wood-pressed and virgin coconut oil made at home.",
        readyLine: "coconut products ready for you.",
        tagline: "Pure coconut oil, made at home",
        footerNote: "🥥 Pure coconut oil, made at home.",
        guestLine: "Pure coconut oil, made at home",
        accountLine: "Your account",
        shopperName: "Customer",
        welcome: "Welcome back 🥥",
        cartTitle: "Your basket",
        cartEmptyIcon: "🥥",
        checkoutHint: "Tell us where to deliver your order.",
        orderDoneLine: "Your order is being packed with care.",
        reviewIntro: "Your feedback helps other shoppers choose.",
        infoTitle: "Help",
        infoLinks: ["Ordering", "Delivery", "Contact", "Returns"],
        detailIntro: "Pressed in small batches from sun-dried Kerala coconuts.",
        detailBadge: ["🪵", "Wood pressed", "No chemicals"],
        detailNote: "Coconut oil turns solid below 24°C; warm the bottle to melt it",
        aboutTitle: "About this product",
        aboutText: "Store in a cool, dry place away from sunlight. Use a dry spoon.",
        care: [["🥥", "Kerala Coconuts", "Sun-dried copra."], ["🪵", "Wood Pressed", "Slow pressed in a chekku."], ["🚫", "Nothing Added", "No chemicals or mixing."]],
        categoryIcons: {"Wood-Pressed Coconut Oil": "🥥", "Virgin Coconut Oil": "✨", "Herbal Hair Oils": "💆", "Coconut Soaps & Skin Care": "🧼", "Coconut Foods & Combos": "🍽️"},
        occasionsTitle: "Shop by use",
        occasionsEyebrow: "🥥 Pure & homemade",
        occasionsSubtitle: "For cooking, hair, skin and the kitchen.",
        occasions: [["🍳", "Cooking"], ["✨", "Virgin"], ["💆", "Hair Oil"], ["👶", "Baby"], ["🧼", "Soap"], ["💋", "Lip Balm"], ["🥥", "Coconut Chips"], ["🎁", "Combo"]],
        budgetPrefix: "Under",
        budgets: [199, 399, 699],
        completeTitle: "Add a hair oil or soap",
    },
    cleaning: {
        color: "#0891b2",
        gradient: ["#0891b2", "#22c55e"],
        badgeTop: "Kills",
        badgeBottom: "99.9% germs",
        icon: "🧽",
        item: "product",
        items: "cleaning products",
        heroEyebrow: "TOILET • BATHROOM • FLOOR • FRESHENERS",
        heroCta: "Shop cleaners",
        heroStats: ["✨ Tough on stains", "📦 Bulk packs"],
        searchPlaceholder: "Search toilet cleaner, tile cleaner, phenyl & mops",
        searchPrompt: "Find cleaners for every corner",
        featuredTitle: "Bestselling cleaners",
        featuredText: "Toilet, bathroom and floor cleaners that work fast.",
        readyLine: "cleaning products ready to ship.",
        tagline: "Sparkling bathrooms, every day",
        footerNote: "🧽 Sparkling bathrooms, every day.",
        guestLine: "Sparkling bathrooms, every day",
        accountLine: "Your account",
        shopperName: "Customer",
        welcome: "Welcome back ✨",
        cartTitle: "Your cart",
        cartEmptyIcon: "🧽",
        checkoutHint: "Tell us where to deliver your order.",
        orderDoneLine: "Your order is being packed with care.",
        reviewIntro: "Your feedback helps other shoppers choose.",
        infoTitle: "Help",
        infoLinks: ["Ordering", "Delivery", "Contact", "Returns"],
        detailIntro: "Tested on hard water stains, germs and grime.",
        detailBadge: ["✨", "Tough on stains", "Fresh fragrance"],
        detailNote: "Wear gloves and keep away from children",
        aboutTitle: "About this product",
        aboutText: "Do not mix with other cleaners. Store in a cool place, cap closed.",
        care: [["✨", "Stain Removal", "Hard water and yellow stains."], ["🌿", "Fresh", "Floral and citrus fragrances."], ["📦", "Bulk", "5 L packs for offices."]],
        categoryIcons: {"Toilet Cleaners": "🚽", "Bathroom & Tile Cleaners": "🛁", "Floor Cleaners & Phenyl": "🧹", "Brushes, Mops & Tools": "🪣", "Fresheners & Combos": "🌸"},
        occasionsTitle: "Shop by area",
        occasionsEyebrow: "🧽 Clean every corner",
        occasionsSubtitle: "Toilet, bathroom, floors and fresh air.",
        occasions: [["🚽", "Toilet"], ["🛁", "Tile"], ["🪟", "Glass"], ["🧹", "Floor"], ["🧪", "Phenyl"], ["🪣", "Mop"], ["🌸", "Freshener"], ["📦", "Kit"]],
        budgetPrefix: "Under",
        budgets: [149, 299, 499],
        completeTitle: "Add a brush or freshener",
    },
    artjewellery: {
        color: "#db2777",
        gradient: ["#db2777", "#f59e0b"],
        badgeTop: "Handmade",
        badgeBottom: "& lightweight",
        icon: "💖",
        item: "piece",
        items: "jewellery",
        heroEyebrow: "EARRINGS • NECKLACES • BRACELETS • RINGS",
        heroCta: "Shop jewellery",
        heroStats: ["✋ Handmade", "🪶 Lightweight"],
        searchPlaceholder: "Search earrings, resin, beaded necklaces & bracelets",
        searchPrompt: "Find handmade jewellery you will love",
        featuredTitle: "Most loved pieces",
        featuredText: "Handmade earrings, necklaces and bracelets.",
        readyLine: "pieces ready to wear.",
        tagline: "Handmade jewellery for every day",
        footerNote: "💖 Handmade jewellery for every day.",
        guestLine: "Handmade jewellery for every day",
        accountLine: "Your account",
        shopperName: "Customer",
        welcome: "Welcome back 💖",
        cartTitle: "Your jewellery bag",
        cartEmptyIcon: "💖",
        checkoutHint: "Tell us where to deliver your order.",
        orderDoneLine: "Your order is being packed with care.",
        reviewIntro: "Your feedback helps other shoppers choose.",
        infoTitle: "Help",
        infoLinks: ["Ordering", "Delivery", "Contact", "Returns"],
        detailIntro: "Handmade in small batches with nickel-free hooks.",
        detailBadge: ["✋", "Handmade", "Nickel free"],
        detailNote: "Every handmade piece is slightly unique",
        aboutTitle: "About this product",
        aboutText: "Keep away from water and perfume. Store in the pouch.",
        care: [["✋", "Handmade", "Made by hand."], ["🪶", "Lightweight", "Light to wear all day."], ["🎁", "Gift Ready", "Packed in a pouch."]],
        categoryIcons: {"Handmade Earrings": "💖", "Resin & Rattan Earrings": "🌸", "Necklaces & Pendants": "📿", "Bracelets & Bangles": "🟣", "Rings & Anklets": "💍"},
        occasionsTitle: "Shop by style",
        occasionsEyebrow: "💖 Find your style",
        occasionsSubtitle: "Earrings, necklaces, bracelets, rings and anklets.",
        occasions: [["💖", "Earrings"], ["🌸", "Resin"], ["🧺", "Rattan"], ["📿", "Necklace"], ["🟣", "Bead"], ["💍", "Ring"], ["🦶", "Anklet"], ["🔠", "Letter"]],
        budgetPrefix: "Jewellery under",
        budgets: [249, 399, 599],
        completeTitle: "Complete the look",
    },
    wallclocks: {
        color: "#7c3aed",
        gradient: ["#7c3aed", "#ec4899"],
        badgeTop: "Silent",
        badgeBottom: "sweep movement",
        icon: "🕰️",
        item: "clock",
        items: "wall clocks",
        heroEyebrow: "ACRYLIC • RESIN • ROMAN • VINTAGE CLOCKS",
        heroCta: "Shop clocks",
        heroStats: ["🤫 Silent movement", "🖼️ Custom clocks"],
        searchPlaceholder: "Search acrylic, Roman, vintage & pendulum clocks",
        searchPrompt: "Find the clock your wall needs",
        featuredTitle: "Bestselling clocks",
        featuredText: "Acrylic, Roman and minimal wall clocks.",
        readyLine: "clocks ready to hang.",
        tagline: "Clocks that make the wall",
        footerNote: "🕰️ Clocks that make the wall.",
        guestLine: "Clocks that make the wall",
        accountLine: "Your account",
        shopperName: "Customer",
        welcome: "Welcome back 🕰️",
        cartTitle: "Your cart",
        cartEmptyIcon: "🕰️",
        checkoutHint: "Tell us where to deliver your order.",
        orderDoneLine: "Your order is being packed with care.",
        reviewIntro: "Your feedback helps other shoppers choose.",
        infoTitle: "Help",
        infoLinks: ["Ordering", "Delivery", "Contact", "Returns"],
        detailIntro: "Silent sweep movement with a glossy acrylic finish, packed in foam.",
        detailBadge: ["🤫", "Silent movement", "Acrylic finish"],
        detailNote: "Uses one AA battery (not included)",
        aboutTitle: "About this product",
        aboutText: "Wipe with a soft dry cloth. Change the battery once a year.",
        care: [["🤫", "Silent", "Never ticks."], ["✨", "Acrylic", "Glossy dial."], ["🖼️", "Custom", "Photo and name clocks."]],
        categoryIcons: {"Acrylic & Resin Clocks": "✨", "Modern Minimal Clocks": "⚫", "Roman & Metal Clocks": "🏛️", "Vintage & Wooden Clocks": "🪵", "Pendulum & Station Clocks": "🔔"},
        occasionsTitle: "Shop by style",
        occasionsEyebrow: "🕰️ For every room",
        occasionsSubtitle: "Acrylic, modern, Roman and vintage clocks.",
        occasions: [["✨", "Acrylic"], ["⚫", "Minimal"], ["🏛️", "Roman"], ["🪵", "Wooden"], ["🌈", "Kids"], ["🗺️", "World Map"], ["🔔", "Pendulum"], ["🚉", "Station"]],
        budgetPrefix: "Clocks under",
        budgets: [999, 1499, 2499],
        completeTitle: "Pair with a mirror",
    },
    mirrors: {
        color: "#0f766e",
        gradient: ["#0f766e", "#d4a017"],
        badgeTop: "HD glass",
        badgeBottom: "custom sizes",
        icon: "🪞",
        item: "mirror",
        items: "mirrors",
        heroEyebrow: "ROUND • SUNBURST • BATHROOM • FULL LENGTH",
        heroCta: "Shop mirrors",
        heroStats: ["🪞 HD glass", "📏 Custom sizes"],
        searchPlaceholder: "Search round, sunburst, bathroom & full-length mirrors",
        searchPrompt: "Find the mirror for your space",
        featuredTitle: "Bestselling mirrors",
        featuredText: "Round, sunburst and full-length mirrors.",
        readyLine: "mirrors ready to hang.",
        tagline: "Mirrors that open up your space",
        footerNote: "🪞 Mirrors that open up your space.",
        guestLine: "Mirrors that open up your space",
        accountLine: "Your account",
        shopperName: "Customer",
        welcome: "Welcome back 🪞",
        cartTitle: "Your cart",
        cartEmptyIcon: "🪞",
        checkoutHint: "Tell us where to deliver your order.",
        orderDoneLine: "Your order is being packed with care.",
        reviewIntro: "Your feedback helps other shoppers choose.",
        infoTitle: "Help",
        infoLinks: ["Ordering", "Delivery", "Contact", "Returns"],
        detailIntro: "5 mm HD glass, crate-packed and delivered ready to hang.",
        detailBadge: ["🪞", "HD glass", "Ready to hang"],
        detailNote: "Custom sizes take 5-7 days",
        aboutTitle: "About this product",
        aboutText: "Clean with a glass cleaner and a microfibre cloth.",
        care: [["🪞", "HD Glass", "No distortion."], ["📏", "Custom", "Any size cut to order."], ["🛡️", "Safe Packing", "Crate-packed."]],
        categoryIcons: {"Round Mirrors": "⭕", "Sunburst & Decorative Mirrors": "☀️", "Bathroom & Vanity Mirrors": "🛁", "Full Length Mirrors": "🧍", "Vintage & Acrylic Mirror Decor": "👑"},
        occasionsTitle: "Shop by style",
        occasionsEyebrow: "🪞 For every wall",
        occasionsSubtitle: "Round, sunburst, vanity and full-length mirrors.",
        occasions: [["⭕", "Round"], ["☀️", "Sunburst"], ["🛁", "Bathroom"], ["💄", "Vanity"], ["🧍", "Full Length"], ["👑", "Baroque"], ["🔷", "Hexagon"], ["✨", "Gold"]],
        budgetPrefix: "Mirrors under",
        budgets: [1999, 2999, 4999],
        completeTitle: "Pair with a wall clock",
    },
    candles: {
        color: "#b45309",
        gradient: ["#b45309", "#f472b6"],
        badgeTop: "Hand-poured",
        badgeBottom: "soy wax",
        icon: "🕯️",
        item: "candle",
        items: "candles",
        heroEyebrow: "JAR CANDLES • PILLARS • SHAPED CANDLES • GIFT SETS",
        heroCta: "Shop candles",
        heroStats: ["🌿 Soy wax", "🔥 40+ hour burn"],
        searchPlaceholder: "Search jar candles, pillars, tealights & gift sets",
        searchPrompt: "Find the scent for your space",
        featuredTitle: "Bestselling candles",
        featuredText: "Hand-poured soy wax candles in our most loved scents.",
        readyLine: "candles ready to glow.",
        tagline: "Hand-poured scented candles",
        footerNote: "🕯️ Hand-poured scented candles.",
        guestLine: "Hand-poured scented candles",
        accountLine: "Your account",
        shopperName: "Customer",
        welcome: "Welcome back 🕯️",
        cartTitle: "Your candle bag",
        cartEmptyIcon: "🕯️",
        checkoutHint: "Tell us where to deliver your order.",
        orderDoneLine: "Your candles are being packed with care.",
        reviewIntro: "Your feedback helps other shoppers choose.",
        infoTitle: "Help",
        infoLinks: ["Ordering", "Delivery", "Contact", "Returns"],
        detailIntro: "Hand-poured in small batches with natural soy wax and cotton wicks.",
        detailBadge: ["🌿", "Soy wax", "Clean burn"],
        detailNote: "Trim the wick to 5 mm before every burn",
        aboutTitle: "About this candle",
        aboutText: "Burn for 2-3 hours at a time. Keep away from drafts, children and pets.",
        care: [["🌿", "Soy Wax", "Clean and natural."], ["🔥", "Long Burn", "Up to 40 hours."], ["🎁", "Gift Ready", "Boxed with a note."]],
        categoryIcons: {"Scented Jar Candles": "🫙", "Pillar & Taper Candles": "🕯️", "Shaped & Decorative Candles": "🌙", "Tealights & Festival Candles": "🪔", "Gift Sets & Wax Melts": "🎁"},
        occasionsTitle: "Shop by mood",
        occasionsEyebrow: "🕯️ Light up your space",
        occasionsSubtitle: "Jar candles, pillars, shaped candles and gift sets.",
        occasions: [["🫙", "Jar"], ["🕯️", "Pillar"], ["🌙", "Moon"], ["🐚", "Shell"], ["💗", "Heart"], ["🪔", "Tealight"], ["🎄", "Christmas"], ["🎁", "Gift"]],
        budgetPrefix: "Candles under",
        budgets: [399, 699, 999],
        completeTitle: "Add tealights or a gift box",
    },
};

export const storeTypeOf = (template) => TYPES[template] || TYPES.plants;

// The wording for the store being shown: its type's, with what the store changes (config.typeText: international
// demo stores use their country's wording, chips and price tiles)
export default function useStoreType() {
    const config = useSelector(selectStoreConfig);
    const type = storeTypeOf(config && config.template);
    return useMemo(() => (config && config.typeText ? { ...type, ...config.typeText } : type), [type, config]);
}

// The emoji shown before a category name; the type's own icon for categories the owner added
export const categoryIcon = (type, category) => type.categoryIcons[category] || type.icon;
